import React, { useMemo, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Card,
    Grid,
    Divider,
    Chip,
    IconButton, Checkbox, Tooltip,
    Icon, Alert, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete, TextField
} from "@mui/material";
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';

// Other imports
import PropTypes from "prop-types";
import EditIcon from "@mui/icons-material/Edit";
import FlightIcon from "@mui/icons-material/Flight";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import StatusBadge from "components/StatusBadge";
import { useTheme } from "@mui/material/styles";
import PageHeader from "components/v2/PageHeader";
import KeyFieldStrip from "components/v2/KeyFieldStrip";
import { v2 as v2t } from "styles/tokens/v2";
import MonoText from "components/MonoText";
import PearlLogoLoader from "components/PearlLogoLoader";
import ItineraryPreviewDialog from "components/ItineraryPreviewDialog";
import PaymentScheduleList from "components/PaymentScheduleList";
import SendPaymentLinkModal from "components/SendPaymentLinkModal";
import { useAuth } from "auth/Auth";
import { previewPrivateObject } from "utils/S3";
import { getApiErrorMessage } from "utils/apiErrors";

import {
    Collapse,
    CircularProgress,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Box from "@mui/material/Box";
import { GENDER_OPTIONS, SPECIAL_DETAILS_MAX_LENGTH } from "./hardcodedData";
import { countTravelerTypes, formatTravelerMix, getTravelerType } from "./TravelOrderUtility";
const CANCELLATION_TYPE_MAP = {
    "By Customer": "customer",
    "By Organization": "organization",
    "Non-Payment": "non_payment",
};

const TIMELINE_DUE_LABELS = {
    payment: "Due by",
    travel_start: "Departs on",
    travel_completed: "Returns on",
    supplier_approval: "Expected by",
    default: "Due by",
};

const getDueLabel = (type) => TIMELINE_DUE_LABELS[type] || TIMELINE_DUE_LABELS.default;

// Helper: presentation-only label + color mapping for the status chip.
// Keeps line.status as the source of truth; only the display changes.
const getLineStatusChipProps = (status) => {
    if (status === "Waiting For Merchant Acknowledgment") {
        return { label: "Pending", color: "warning" };
    }
    if (status === "Booked") {
        return { label: "Booked", color: "success" };
    }
    if (status === "Cancelled") {
        return { label: "Cancelled", color: "error" };
    }
    return { label: status || "Pending", color: "default" };
};

const ORDER_STATUS_OPTIONS = [
    { value: "draft", status: "Draft", sub_status: "Draft", label: "Draft" },
    { value: "payment_pending", status: "Secured", sub_status: "Payment Pending", label: "Payment Pending" },
    { value: "booking_in_progress", status: "Booking Confirmed", sub_status: "Booking In Progress", label: "Booking Confirmed - Waiting Supplier confirmation" },
    { value: "ready_to_travel", status: "Booking Confirmed", sub_status: "Ready to Travel", label: "Ready to Travel" },
    { value: "travel_in_progress", status: "Travel In Progress", sub_status: "Travel In Progress", label: "Travel In Progress" },
    { value: "travel_completed", status: "Travel Completed", sub_status: "Travel Completed", label: "Travel Completed" },
    { value: "cancelled_by_customer", status: "Cancelled", sub_status: "Cancelled by Customer", label: "Cancelled by Customer" },
    { value: "cancelled_by_organization", status: "Cancelled", sub_status: "Cancelled by Organization", label: "Cancelled by Organization" },
    { value: "failed", status: "Failed", sub_status: "Payment Failed", label: "Payment Failed" },
];
const NON_CANCELLABLE_STATUSES = ["Cancelled", "Failed", "Travel Completed", "Travel In Progress"];

/** Default page-layout sections for Travel Order detail (prefs key: travelorder). */
const DEFAULT_TRAVEL_ORDER_SECTIONS = [
    "Booking Information",
    "Primary Traveler Information",
    "Trip Package Information",
    "Travelers Information",
    "Add-on Services & Pricing Details",
    "Order Cancellation",
    "Merchant Order Lines",
    "Audit Information",
    "Booking Timeline",
];

/** Right-rail sections stay in the sidebar; everything else fills the main column. */
const TRAVEL_ORDER_SIDEBAR_SECTIONS = ["Booking Timeline"];

const mapPaymentStatus = (status) => {
    const s = (status || "").toLowerCase().trim();
    if (["paid", "success", "completed"].includes(s)) return "paid";
    if (["overdue", "late", "failed"].includes(s)) return "overdue";
    if (["pending", "payment pending", "due", "unpaid", "processing"].includes(s)) return "pending";
    return s;
};

const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

function LineItem({
    icon,
    label,
    amount,
    nights,
    paxCount,
    bold,
    formatAmount,
    cancelled,
    subtitle,
    iconBg,
    iconColor,
}) {
    const hasNights = nights != null && nights > 0;
    const hasPax = paxCount != null && paxCount > 0;
    const showBreakdown = Boolean(subtitle) || hasPax;

    const total = subtitle != null ? amount : amount * (hasNights ? nights : 1) * (hasPax ? paxCount : 1);
    const isNegative = total < 0;
    const strike = cancelled ? { textDecoration: "line-through" } : {};

    const breakdownParts = [];
    if (!subtitle) {
        breakdownParts.push(formatAmount(Math.abs(amount)));
        if (hasNights) breakdownParts.push(`${nights} night${nights > 1 ? "s" : ""}`);
        if (hasPax) breakdownParts.push(`${paxCount} pax`);
    }

    return (
        <MDBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={1.5}
            py={0.75}
            sx={{
                opacity: cancelled ? 0.55 : 1,
            }}
        >
            <MDBox display="flex" alignItems="center" gap={1.25} minWidth={0}>
                <MDBox
                    sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "8px",
                        bgcolor: iconBg || "#F1F5F9",
                        color: iconColor || "#5C6B82",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}
                >
                    <Icon sx={{ fontSize: 18, color: "inherit" }}>{icon}</Icon>
                </MDBox>
                <MDBox minWidth={0}>
                    <MDBox display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                        <MDTypography
                            variant="button"
                            fontWeight={bold ? "bold" : "medium"}
                            color="dark"
                            noWrap
                            sx={{ fontSize: "0.825rem", ...strike }}
                        >
                            {label}
                        </MDTypography>
                        {cancelled && (
                            <Chip
                                label="Cancelled"
                                size="small"
                                sx={{
                                    bgcolor: "#FDECEA",
                                    color: "#C62828",
                                    fontWeight: 700,
                                    fontSize: "0.65rem",
                                    height: 18,
                                    px: 0.5,
                                }}
                            />
                        )}
                    </MDBox>
                    {showBreakdown && (
                        <MDBox display="flex" alignItems="center" gap={0.5} mt={0.15}>
                            {!subtitle && hasPax && <Icon sx={{ fontSize: 13, color: "#8A97AC" }}>person</Icon>}
                            <MDTypography variant="caption" color="text" sx={{ fontSize: "0.72rem", ...strike }}>
                                {subtitle || breakdownParts.join(" × ")}
                            </MDTypography>
                        </MDBox>
                    )}
                </MDBox>
            </MDBox>
            <MDTypography
                variant="button"
                fontWeight={bold || isNegative ? "bold" : "medium"}
                color={isNegative ? "success" : "dark"}
                sx={{ whiteSpace: "nowrap", fontSize: "0.85rem", ...strike }}
            >
                {isNegative ? `- ${formatAmount(Math.abs(total))}` : formatAmount(total)}
            </MDTypography>
        </MDBox>
    );
}

LineItem.propTypes = {
    icon: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    amount: PropTypes.number.isRequired,
    nights: PropTypes.number,
    paxCount: PropTypes.number,
    bold: PropTypes.bool,
    formatAmount: PropTypes.func.isRequired,
    cancelled: PropTypes.bool,
    subtitle: PropTypes.node,
    iconBg: PropTypes.string,
    iconColor: PropTypes.string,
};

function SummaryLine({ label, value, bold }) {
    return (
        <MDBox display="flex" justifyContent="space-between" alignItems="center" py={0.5}>
            <MDTypography variant={bold ? "button" : "caption"} color="text" fontWeight={bold ? "bold" : "regular"}>
                {label}
            </MDTypography>
            <MDTypography variant={bold ? "button" : "caption"} fontWeight="bold" color={bold ? "dark" : "text"}>
                {value}
            </MDTypography>
        </MDBox>
    );
}

SummaryLine.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.node.isRequired,
    bold: PropTypes.bool,
};

function DetailField({ label, value, xs = 12, md = 4 }) {
    return (
        <Grid item xs={xs} md={md}>
            <MDTypography variant="caption" color="text" fontWeight="medium" display="block" mb={0.5}>
                {label}
            </MDTypography>
            <MDTypography variant="button" fontWeight="regular" color="dark">
                {value || "—"}
            </MDTypography>
        </Grid>
    );
}

function FieldLabel({ label, required, hint }) {
    return (
        <Box mb={0.5}>
            <MDTypography variant="caption" fontWeight="bold" color="text" display="block">
                {label}
                {required && (
                    <Box component="span" sx={{ color: "#E53935", ml: 0.25 }}>
                        *
                    </Box>
                )}
            </MDTypography>
            {hint && (
                <MDTypography variant="caption" color="text" opacity={0.8}>
                    {hint}
                </MDTypography>
            )}
        </Box>
    );
}


function InfoCard({ icon, title, headerAction, children, minHeight }) {
    // v2: a white card header with the 15px card title, as on the other records.
    const isV2 = useTheme()?.pearlUi === "v2";
    return (
        <Card sx={{ borderRadius: "12px", border: "1px solid #E8EDF5", boxShadow: "none", height: "100%", overflow: "hidden", }}>
            <MDBox
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                px={2}
                py={isV2 ? 1.75 : 1.25}
                sx={{
                    bgcolor: isV2 ? "transparent" : "#F4F6F9",
                    borderBottom: isV2 ? `1px solid ${v2t.border}` : "1px solid #E8EDF5",
                }}
            >
                <MDBox display="flex" alignItems="center" gap={1}>
                    <Icon sx={{ fontSize: isV2 ? 18 : 20, color: isV2 ? v2t.text2 : "#5C6B82" }}>{icon}</Icon>
                    <MDTypography variant="button" fontWeight="bold" color="dark" sx={isV2 ? { fontSize: 15, fontWeight: 600 } : undefined}>
                        {title}
                    </MDTypography>
                </MDBox>
                {headerAction}
            </MDBox>
            <MDBox p={2.5} sx={{ minHeight }}>
                {children}
            </MDBox>
        </Card>
    );
}

InfoCard.propTypes = {
    icon: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    headerAction: PropTypes.node,
    children: PropTypes.node,
    minHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};


function TravelersTable({
    travelers,
    selectable = false,
    selectedIds = [],
    onSelectionChange = () => { },
}) {
    const navigate = useNavigate();

    const columnHeader = selectable
        ? ["", "TRAVELER NAME", "TYPE", "AGE", "PASSPORT NO."]
        : ["NO.", "TRAVELER NAME", "TYPE", "AGE", "PASSPORT NO."]

    const handleViewPax = (order) => {
        navigate(`/travel-order-pax/${order.id}`);
    };

    if (!travelers?.length) {
        return (
            <MDTypography variant="body2" color="text">
                No traveler details available.
            </MDTypography>
        );
    }

    const selectableIds = travelers.filter((t) => !t.is_cancelled).map((t) => t.id);
    const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id));
    const someSelected = selectableIds.some((id) => selectedIds.includes(id));

    const handleToggleAll = () => {
        if (allSelected) {
            onSelectionChange(selectedIds.filter((id) => !selectableIds.includes(id)));
        } else {
            onSelectionChange(Array.from(new Set([...selectedIds, ...selectableIds])));
        }
    };

    const handleToggleOne = (id) => {
        if (selectedIds.includes(id)) {
            onSelectionChange(selectedIds.filter((sid) => sid !== id));
        } else {
            onSelectionChange([...selectedIds, id]);
        }
    };

    const gridTemplateColumns = selectable
        ? "40px 48px 2fr 1fr 0.8fr 2fr"
        : "48px 2fr 1fr 0.8fr 2fr";

    return (
        <MDBox sx={{ border: "1px solid #E8EDF5", borderRadius: "0.75rem", overflow: "auto" }}>
            <MDBox
                sx={{
                    display: "grid",
                    gridTemplateColumns,
                    bgcolor: "#F4F6F9",
                    borderBottom: "1px solid #E8EDF5",
                    px: 2,
                    py: 1.25,
                    minWidth: "max-content",
                    alignItems: "center",
                }}
            >
                {selectable && (
                    <Checkbox
                        size="small"
                        checked={allSelected}
                        indeterminate={someSelected && !allSelected}
                        onChange={handleToggleAll}
                        sx={{ p: 0 }}
                    />
                )}
                {columnHeader.map((col) => (
                    <MDTypography
                        key={col}
                        variant="caption"
                        fontWeight="bold"
                        color="text"
                        sx={{ letterSpacing: "0.03em" }}
                    >
                        {col}
                    </MDTypography>
                ))}
            </MDBox>

            {travelers.map((traveler, index) => {
                const isChecked = selectedIds.includes(traveler.id);
                const cancelled = Boolean(traveler.is_cancelled);

                return (
                    <MDBox
                        key={traveler.id}
                        sx={{
                            display: "grid",
                            opacity: cancelled ? 0.55 : 1,
                            gridTemplateColumns,
                            alignItems: "center",
                            px: 2,
                            py: 1.5,
                            minWidth: "max-content",
                            borderBottom: index === travelers.length - 1 ? "none" : "1px solid #E8EDF5",
                            "&:hover": { bgcolor: "#FAFBFD" },
                        }}
                    >
                        {selectable && (
                            <Checkbox
                                size="small"
                                disabled={cancelled}
                                checked={isChecked}
                                onChange={() => handleToggleOne(traveler.id)}
                                onClick={(e) => e.stopPropagation()}
                                sx={{ p: 0 }}
                            />
                        )}

                        {
                            selectable
                                ? <MDTypography></MDTypography>
                                : <MDTypography variant="button" color="text">
                                    {index + 1}
                                </MDTypography>
                        }

                        <MDBox display="flex" alignItems="center" gap={0.75} minWidth={0}>
                            <MDTypography
                                variant="button"
                                fontWeight="bold"
                                color="info"
                                noWrap
                                sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                                onClick={() => handleViewPax(traveler)}
                            >
                                {traveler.name}
                            </MDTypography>
                            {traveler.is_primary && (
                                <Chip
                                    label="Primary"
                                    size="small"
                                    sx={{ bgcolor: "#E3F2FD", color: "#1565C8", fontWeight: 600, fontSize: "0.65rem", height: 20 }}
                                />
                            )}
                            {cancelled && (
                                <Chip
                                    label="Cancelled"
                                    size="small"
                                    sx={{ bgcolor: "#FDECEA", color: "#C62828", fontWeight: 600, fontSize: "0.65rem", height: 20 }}
                                />
                            )}
                        </MDBox>

                        <MDTypography variant="button" color="text">
                            {getTravelerType(traveler)}
                        </MDTypography>

                        <MDTypography variant="button" color="text">
                            {traveler.age ?? "—"}
                        </MDTypography>

                        <MDTypography variant="button" color="text">
                            {traveler.passport_id ? <MonoText>{traveler.passport_id}</MonoText> : "—"}
                        </MDTypography>
                    </MDBox>
                );
            })}
        </MDBox>
    );
}

TravelersTable.propTypes = {
    travelers: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            name: PropTypes.string,
            age: PropTypes.number,
            passenger_type: PropTypes.oneOf(["Adult", "Child"]),
            passport_id: PropTypes.string,
            is_primary: PropTypes.bool,
        })
    ),
};

const TIMELINE_EVENT_STYLES = {
    order_created: { icon: "flag", color: "#0E2348", filled: true },
    payment: { icon: "attach_money", color: "#2E7D32", filled: true },
    supplier_approval: { icon: "verified", color: "#1565C8", filled: true },
    travel_start: { icon: "flight_takeoff", color: "#7B1FA2", filled: true },
    travel_completed: { icon: "flight_land", color: "#2E7D32", filled: true },
    cancelled: { icon: "cancel", color: "#C62828", filled: true },
    default: { icon: null, color: "#5C6B82", filled: false },
};


function BookingTimeline({ events, navigate }) {
    if (!events?.length) {
        return (
            <MDTypography variant="caption" color="text" sx={{ fontStyle: "italic", opacity: 0.7 }}>
                No timeline events yet.
            </MDTypography>
        );
    }

    // The "next" step is the first event that isn't done — derived from
    // position rather than trusting the raw status string, so the UI stays
    // correct even if the backend hasn't marked the following event
    // "pending" (e.g. still says "upcoming" right after a payment lands).
    const nextIndex = events.findIndex(
        (e) => !["completed", "cancelled", "skipped"].includes(e.status)
    );

    return (
        <MDBox display="flex" flexDirection="column">
            {events.map((event, index) => {
                const style = TIMELINE_EVENT_STYLES[event.type] || TIMELINE_EVENT_STYLES.default;
                const isLast = index === events.length - 1;
                const isDone = event.status === "completed" || event.status === "cancelled";
                const isNext = index === nextIndex;
                const isMuted = !isDone && !isNext;
                // The line leaving a node should only be colored if the node it's
                // connecting INTO is also part of the completed path — otherwise a
                // cancelled order's red line would visually "reach into" a step that
                // never happened.
                const nextEvent = events[index + 1];
                const lineColor = isDone && nextEvent?.status !== "skipped" ? style.color : "#E8EDF5";

                return (
                    <MDBox key={event.id} display="flex" gap={1.5} sx={{ opacity: isMuted ? 0.55 : 1 }}>
                        <MDBox display="flex" flexDirection="column" alignItems="center" sx={{ width: 28, flexShrink: 0 }}>
                            {/* Icon container */}
                            <MDBox
                                sx={{
                                    width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    overflow: "hidden",
                                    bgcolor: isDone ? style.color : "#fff",
                                    border: `2px solid ${isNext ? style.color : isDone ? style.color : "#CBD5E1"}`,
                                    boxSizing: "border-box",
                                }}
                            >
                                {/* Icon */}
                                {style.icon && (
                                    <Icon sx={{ fontSize: "16px !important", width: 16, height: 16, color: isDone ? "#fff" : isNext ? style.color : "#B0BAC9" }}>
                                        {style.icon}
                                    </Icon>
                                )}
                            </MDBox>
                            {/* Line joining icons */}
                            {!isLast && (
                                <MDBox
                                    sx={{
                                        width: 2, flex: 1, minHeight: 32,
                                        bgcolor: lineColor,
                                        my: 0.25,
                                        transition: "background-color 0.2s ease",
                                    }}
                                />
                            )}
                        </MDBox>

                        <MDBox flex={1} pb={isLast ? 0 : 2.5} minWidth={0}>
                            <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                                <MDTypography variant="button" fontWeight="bold" color={isDone || isNext ? "dark" : "text"}>
                                    {event.title}
                                </MDTypography>

                                {isDone && event.type === "payment" ? (
                                    <MDBox display="flex" flexDirection="column" alignItems="flex-end" gap={0.25} flexShrink={0}>
                                        {event.date && (
                                            <MDTypography variant="caption" color="text" sx={{ whiteSpace: "nowrap" }}>
                                                {formatDate(event.date)}
                                            </MDTypography>
                                        )}
                                        <Chip
                                            label="Paid"
                                            size="small"
                                            sx={{
                                                bgcolor: "#E8F5E9",
                                                color: "#2E7D32",
                                                fontWeight: 700,
                                                fontSize: "0.65rem",
                                                height: 18,
                                            }}
                                        />
                                    </MDBox>
                                ) : (
                                    <MDTypography variant="caption" color="text" sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                                        {isDone
                                            ? formatDate(event.date)
                                            : event.due_date
                                                ? `${getDueLabel(event.type)} ${formatDate(event.due_date)}`
                                                : (isNext && event.type === "payment" ? "Pending" : "")}
                                    </MDTypography>
                                )}
                            </MDBox>
                            {event.subtitle && (
                                <MDTypography variant="caption" color="text" display="block" mt={0.25}>
                                    {event.subtitle}
                                </MDTypography>
                            )}
                            {event.type === "payment" && event.payment_transaction_id && (
                                <MDTypography
                                    variant="caption"
                                    color="info"
                                    display="block"
                                    mt={0.25}
                                    sx={{ cursor: "pointer", fontWeight: 600, "&:hover": { textDecoration: "underline" } }}
                                    onClick={() => navigate(`/payment-transaction/${event.payment_transaction_id}`)}
                                >
                                    {event.payment_transaction_name || `PT-${event.payment_transaction_id}`}
                                </MDTypography>
                            )}
                        </MDBox>
                    </MDBox>
                );
            })}
        </MDBox>
    );
}


BookingTimeline.propTypes = {
    events: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            type: PropTypes.string,
            title: PropTypes.string.isRequired,
            subtitle: PropTypes.string,
            time: PropTypes.string,
            payment_transaction_id: PropTypes.number,
            payment_transaction_name: PropTypes.string,
        })
    ),
    navigate: PropTypes.func,
};


// ---------------------------------------------------------------------------
// ADD-ON SUPPLEMENTS: visually distinct from the base booking (teal accent).
// ---------------------------------------------------------------------------
const SUPPLEMENT_THEME = { color: "#00838F", bg: "#E0F7FA", border: "#B2EBF2", surface: "#F5FDFE" };

function SupplementChip({ label = "Supplement", sx }) {
    return (
        <Chip
            label={label}
            size="small"
            sx={{
                bgcolor: SUPPLEMENT_THEME.bg,
                color: SUPPLEMENT_THEME.color,
                fontWeight: 700,
                fontSize: "0.65rem",
                height: 18,
                ...sx,
            }}
        />
    );
}

SupplementChip.propTypes = {
    label: PropTypes.string,
    sx: PropTypes.object,
};

function SupplementRailCard({ supplements, payment, formatAmount, navigate, onSendPaymentEmail }) {
    const list = supplements || payment?.supplements || [];
    if (!list.length) return null;

    const isCancelled = (s) => s?.status === "Cancelled" || s?.payment_status === "Cancelled";

    const statusOf = (s) => {
        if (isCancelled(s)) return { label: "Cancelled", color: "#C62828", bg: "#FDECEA" };
        if (s.payment_status === "Paid") return { label: "Paid", color: "#2E7D32", bg: "#E8F5E9" };
        return { label: "Pending", color: "#E68A06", bg: "#FFF3E0" };
    };

    const activeSupplements = list.filter((s) => !isCancelled(s));
    const supplementTotal = Number(
        payment?.supplement_total ??
        activeSupplements.reduce((sum, s) => sum + Number(s.total || 0), 0)
    );
    const supplementPaid = Number(
        payment?.supplement_paid ??
        activeSupplements.reduce((sum, s) => sum + Number(s.paid_amount || 0), 0)
    );
    const supplementPending = Number(
        payment?.supplement_pending ??
        activeSupplements.reduce((sum, s) => sum + Number(s.pending_amount || 0), 0)
    );

    return (
        <MDBox display="flex" flexDirection="column" gap={1.5}>
            {list.map((s) => {
                const chip = statusOf(s);
                const cancelled = isCancelled(s);
                const pending = Number(s.pending_amount || 0);
                const strike = cancelled ? { textDecoration: "line-through" } : {};
                const visibleLines = (s.lines || []).filter(
                    (line) => !cancelled || Number(line.amount || 0) > 0 || line.is_cancelled
                );
                const hasLines = visibleLines.length > 0;
                const names = (s.lines || []).map((l) => l.name).join(", ");

                return (
                    <MDBox
                        key={s.invoice_id}
                        sx={{
                            border: `1px dashed ${SUPPLEMENT_THEME.border}`,
                            bgcolor: SUPPLEMENT_THEME.surface,
                            borderRadius: 1.5,
                            p: 1.5,
                            opacity: cancelled ? 0.75 : 1,
                        }}
                    >
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" gap={1} mb={hasLines ? 1 : 0.5}>
                            <MDBox display="flex" alignItems="center" gap={0.75} flexWrap="wrap" minWidth={0}>
                                <MDTypography variant="button" fontWeight="bold" color="dark">
                                    {s.sequence ? `Supplement ${s.sequence}` : "Supplement"}
                                </MDTypography>
                                {s.invoice_number && (
                                    <MDTypography
                                        variant="caption"
                                        color="info"
                                        sx={{ cursor: "pointer", fontWeight: 600, "&:hover": { textDecoration: "underline" } }}
                                        onClick={() => navigate(`/invoices/${s.invoice_id}`)}
                                    >
                                        {s.invoice_number}
                                    </MDTypography>
                                )}
                            </MDBox>
                            <MDBox display="flex" alignItems="center" gap={0.5} flexShrink={0}>
                                <Chip
                                    label={chip.label}
                                    size="small"
                                    sx={{ bgcolor: chip.bg, color: chip.color, fontWeight: 700, fontSize: "0.65rem", height: 18 }}
                                />
                                {!cancelled && s.payment_status !== "Paid" && s.invoice_id && (
                                    <Tooltip title="Send payment email">
                                        <span>
                                            <IconButton
                                                size="small"
                                                color="info"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onSendPaymentEmail?.(s);
                                                }}
                                                aria-label="Send payment email"
                                                sx={{ p: 0.25 }}
                                            >
                                                <EmailOutlinedIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}
                            </MDBox>
                        </MDBox>

                        {hasLines ? (
                            visibleLines.map((line, i) => (
                                <MDBox key={`${s.invoice_id}-${i}`} display="flex" justifyContent="space-between" alignItems="flex-start" gap={1.5} py={0.5}>
                                    <MDBox minWidth={0}>
                                        <MDTypography variant="button" color="dark" sx={strike}>
                                            {line.name}
                                        </MDTypography>
                                        <MDTypography variant="caption" color="text" display="block" sx={{ fontSize: "0.7rem", ...strike }}>
                                            {line.pricing_basis === "fixed"
                                                ? "Fixed price"
                                                : `${formatAmount(line.unit_price)} × ${line.pax_count} pax`}
                                            {line.traveler_names?.length ? ` · ${line.traveler_names.join(", ")}` : ""}
                                        </MDTypography>
                                    </MDBox>
                                    <MDTypography variant="button" fontWeight="medium" color="dark" sx={{ whiteSpace: "nowrap", ...strike }}>
                                        {formatAmount(line.amount)}
                                    </MDTypography>
                                </MDBox>
                            ))
                        ) : names ? (
                            <MDTypography variant="caption" color="text" display="block" mt={0.5} noWrap>
                                {names}
                            </MDTypography>
                        ) : null}

                        <Divider sx={{ my: 0.75 }} />

                        {Number(s.tax_total) > 0 && <SummaryLine label="Subtotal" value={formatAmount(s.subtotal)} />}
                        {Number(s.tax_total) > 0 && <SummaryLine label="Taxes" value={formatAmount(s.tax_total)} />}
                        {(!cancelled || Number(s.total || 0) > 0) && (
                            <SummaryLine label="Supplement Total" value={formatAmount(Number(s.total || 0))} bold />
                        )}
                        {!cancelled && <SummaryLine label="Paid" value={formatAmount(Number(s.paid_amount || 0))} />}
                        {!cancelled && pending > 0 && <SummaryLine label="Pending" value={formatAmount(pending)} />}

                        <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                            Requested {formatDate(s.created_at)}
                            {!cancelled && s.payment_status !== "Paid" && s.expires_at
                                ? ` · Expires ${formatDate(s.expires_at)}`
                                : ""}
                        </MDTypography>

                        {!cancelled && s.payment_status !== "Paid" && s.expires_at && (
                            <MDTypography variant="caption" color="text" display="block" mt={0.25} sx={{ fontStyle: "italic" }}>
                                Must be paid in full. Link expires {formatDate(s.expires_at)}.
                            </MDTypography>
                        )}
                        {cancelled && (
                            <MDTypography variant="caption" color="text" display="block" mt={0.25} sx={{ fontStyle: "italic" }}>
                                This supplement was cancelled or expired. It is not counted in the totals.
                            </MDTypography>
                        )}

                        {s.payment_transaction_id && (
                            <MDTypography
                                variant="caption"
                                color="info"
                                display="block"
                                mt={0.5}
                                sx={{ cursor: "pointer", fontWeight: 600, "&:hover": { textDecoration: "underline" } }}
                                onClick={() => navigate(`/payment-transaction/${s.payment_transaction_id}`)}
                            >
                                {s.payment_transaction_name || `PT-${s.payment_transaction_id}`}
                            </MDTypography>
                        )}
                    </MDBox>
                );
            })}

            {/* Roll-up: active supplements only, never merged into the base Total Price above */}
            {activeSupplements.length > 0 && supplementTotal > 0 && (
                <MDBox sx={{ mt: 0.5, p: 1.5, borderRadius: 1.5, border: `1px solid ${SUPPLEMENT_THEME.border}`, bgcolor: "#fff" }}>
                    <SummaryLine label="Supplements Total" value={formatAmount(supplementTotal)} bold />
                    {supplementPaid > 0 && <SummaryLine label="Supplements Paid" value={formatAmount(supplementPaid)} />}
                    {supplementPending > 0 && (
                        <SummaryLine label="Supplements Pending" value={formatAmount(supplementPending)} />
                    )}
                    <MDTypography variant="caption" color="text" display="block" mt={0.5} sx={{ fontStyle: "italic", opacity: 0.8 }}>
                        Not included in the booking Total Price, Paid or Pending amounts above.
                    </MDTypography>
                </MDBox>
            )}
        </MDBox>
    );
}

SupplementRailCard.propTypes = {
    supplements: PropTypes.arrayOf(PropTypes.object),
    payment: PropTypes.object,
    formatAmount: PropTypes.func.isRequired,
    navigate: PropTypes.func.isRequired,
    onSendPaymentEmail: PropTypes.func,
};


function QuickActionRow({ icon, label, onClick, danger, bold, isLast, loading, disabled }) {
    const isDisabled = loading || disabled;

    return (
        <MDBox
            onClick={isDisabled ? undefined : onClick}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            px={2}
            py={1.5}
            sx={{
                cursor: isDisabled ? "not-allowed" : "pointer",
                opacity: isDisabled ? 0.5 : 1,
                borderBottom: isLast ? "none" : "1px solid #E8EDF5",
                "&:hover": { bgcolor: isDisabled ? "transparent" : "#FAFBFD" },
                transition: "background-color 0.15s ease",
            }}
        >
            <MDBox display="flex" alignItems="center" gap={1.25}>
                {loading ? (
                    <CircularProgress size={16} thickness={5} sx={{ color: "#1565C8" }} />
                ) : (
                    <Icon sx={{ fontSize: 19, color: isDisabled ? "#8A97AC" : danger ? "#C62828" : "#1565C8" }}>
                        {icon}
                    </Icon>
                )}
                <MDTypography
                    variant="button"
                    fontWeight={bold ? "bold" : "medium"}
                    sx={{ color: isDisabled ? "#8A97AC" : danger ? "#C62828" : "#1A2035" }}
                >
                    {label}
                </MDTypography>
            </MDBox>
            <Icon sx={{ fontSize: 18, color: isDisabled ? "#CBD5E1" : danger ? "#C62828" : "#B0BAC9" }}>
                chevron_right
            </Icon>
        </MDBox>
    );
}

QuickActionRow.propTypes = {
    icon: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    danger: PropTypes.bool,
    bold: PropTypes.bool,
    isLast: PropTypes.bool,
    loading: PropTypes.bool,
    disabled: PropTypes.bool,
};

function PaymentScheduleRow({ label, amount, dueDate, status, formatAmount }) {
    const statusStyle = {
        paid: { label: "Paid", color: "#2E7D32", bg: "#E8F5E9" },
        pending: { label: "Pending", color: "#E68A06", bg: "#FFF3E0" },
        overdue: { label: "Overdue", color: "#C62828", bg: "#FDECEA" },
    }[status] || { label: status, color: "#616161", bg: "#F0F0F0" };

    return (
        <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            py={1}
            sx={{ borderBottom: "1px solid #EFF2F6" }}
        >
            <MDBox>
                <MDTypography variant="button" fontWeight="medium" color="dark" display="block">
                    {label}
                </MDTypography>
                {dueDate && (
                    <MDTypography variant="caption" color="text">
                        Due {formatDate(dueDate)}
                    </MDTypography>
                )}
            </MDBox>
            <MDBox textAlign="right">
                <MDTypography variant="button" fontWeight="bold" color="dark" display="block">
                    {formatAmount(amount)}
                </MDTypography>
                <Chip
                    label={statusStyle.label}
                    size="small"
                    sx={{
                        bgcolor: statusStyle.bg,
                        color: statusStyle.color,
                        fontWeight: 700,
                        fontSize: "0.65rem",
                        height: 18,
                        mt: 0.25,
                    }}
                />
            </MDBox>
        </MDBox>
    );
}

PaymentScheduleRow.propTypes = {
    label: PropTypes.string.isRequired,
    amount: PropTypes.number.isRequired,
    dueDate: PropTypes.string,
    status: PropTypes.string.isRequired,
    formatAmount: PropTypes.func.isRequired,
};

function TravelOrderDetailView() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { axiosInstance } = useAuth();
    const [order, setOrder] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");

    const [taxes, setTaxes] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [paymentSchedule, setPaymentSchedule] = useState([]);
    const [loadingPaymentSchedule, setLoadingPaymentSchedule] = useState(false);
    const [merchantOrderLines, setMerchantOrderLines] = useState([]);
    const [loadingMerchantOrderLines, setLoadingMerchantOrderLines] = useState(false);
    const [generatingMerchantOrderLines, setGeneratingMerchantOrderLines] = useState(false);
    const [orderCancellations, setOrderCancellations] = useState([]);
    const [, setLoadingOrderCancellations] = useState(false);
    const [resendConfirmLine, setResendConfirmLine] = useState(null);
    const [resendingLineId, setResendingLineId] = useState(null);
    const [cancellationDialog, setCancellationDialog] = useState(false);
    const [cancellationInProgress, setCancellationInProgress] = useState(false);
    const [cancellationFormData, setCancellationFormData] = useState({});
    const [cancellationType, setCancellationType] = useState(null);
    const [newPrimaryId, setNewPrimaryId] = useState(null);
    const [cancellationComments, setCancellationComments] = useState("");
    const [cancellationPreview, setCancellationPreview] = useState(null);
    const [cancellationPreviewLoading, setCancellationPreviewLoading] = useState(false);
    const [cancellationPreviewError, setCancellationPreviewError] = useState("");

    const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
    const [loadingItineraryPreview, setLoadingItineraryPreview] = useState(false);
    const [statusMenuOpen, setStatusMenuOpen] = useState(false);
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [statusError, setStatusError] = useState("");
    const [sendingItinerary, setSendingItinerary] = useState(false);
    const [snackbar, setSnackbar] = useState(
        { open: false, message: '', severity: 'success' }
    );
    const [paymentLinkModalOpen, setPaymentLinkModalOpen] = useState(false);
    const [paymentLinkInvoiceId, setPaymentLinkInvoiceId] = useState(null);
    const [paymentLinkDefaultEmail, setPaymentLinkDefaultEmail] = useState("");
    const [preparingPaymentLink, setPreparingPaymentLink] = useState(false);
    const [sectionOrder, setSectionOrder] = useState(DEFAULT_TRAVEL_ORDER_SECTIONS);
    const [hiddenSections, setHiddenSections] = useState([]);

    useEffect(() => {
        const fetchGlobalPreferences = async () => {
            try {
                const res = await axiosInstance.get("/preferences/global");
                const prefs = res.data?.section_preferences?.travelorder;
                if (prefs) {
                    let order = Array.isArray(prefs.order) && prefs.order.length
                        ? prefs.order.filter((s) => DEFAULT_TRAVEL_ORDER_SECTIONS.includes(s))
                        : [...DEFAULT_TRAVEL_ORDER_SECTIONS];
                    const missing = DEFAULT_TRAVEL_ORDER_SECTIONS.filter((s) => !order.includes(s));
                    if (missing.length) {
                        const molIndex = order.indexOf("Merchant Order Lines");
                        if (molIndex !== -1 && missing.includes("Order Cancellation")) {
                            order.splice(molIndex, 0, "Order Cancellation");
                            const remainingMissing = missing.filter((s) => s !== "Order Cancellation");
                            order = [...order, ...remainingMissing];
                        } else {
                            order = [...order, ...missing];
                        }
                    }
                    setSectionOrder(order);
                    setHiddenSections(
                        Array.isArray(prefs.hidden)
                            ? prefs.hidden.filter((s) => DEFAULT_TRAVEL_ORDER_SECTIONS.includes(s))
                            : [],
                    );
                } else {
                    setSectionOrder([...DEFAULT_TRAVEL_ORDER_SECTIONS]);
                    setHiddenSections([]);
                }
            } catch (e) {
                setSectionOrder([...DEFAULT_TRAVEL_ORDER_SECTIONS]);
                setHiddenSections([]);
            }
        };
        fetchGlobalPreferences();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const visibleSections = useMemo(
        () => sectionOrder.filter((key) => !hiddenSections.includes(key)),
        [sectionOrder, hiddenSections],
    );
    const visibleMainSections = useMemo(
        () => visibleSections.filter((key) => !TRAVEL_ORDER_SIDEBAR_SECTIONS.includes(key)),
        [visibleSections],
    );
    const showBookingTimeline = visibleSections.includes("Booking Timeline");
    // UI v2 record layout (design-reference "Travel · Booking record").
    const isV2 = useTheme()?.pearlUi === "v2";

    const orderCurrency = order?.currency || order?.trip?.currency || order?.payment?.currency || "USD";
    const formatAmount = (amount, customCurrency) => {
        const curr = customCurrency || orderCurrency;
        const formatter = new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: curr || "USD",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

        return formatter
            .formatToParts(amount || 0)
            .map((part) =>
                part.type === "currency"
                    ? `${part.value}\u00A0\u00A0`
                    : part.value
            )
            .join("");
    };

    const handleViewItinerary = async () => {
        if (isDraftOrder) return;
        setLoadingItineraryPreview(true);
        try {
            const response = await axiosInstance.get(
                `/travel-orders/${order.id}/itinerary-pdf`,
                { responseType: "blob" }
            );
            const blobUrl = URL.createObjectURL(response.data);
            window.open(blobUrl, "_blank", "noopener,noreferrer");
            // Revoke later so the new tab has time to load it
            setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
        } catch (err) {
            setSnackbar({
                open: true,
                message: err?.response?.status === 404
                    ? "No itinerary is available for this booking yet."
                    : "Failed to load the itinerary PDF. Please try again.",
                severity: "error",
            });
        } finally {
            setLoadingItineraryPreview(false);
        }
    };

    const handleStatusSelect = async (status, subStatus) => {
        if (!subStatus || subStatus === order.sub_status) return;

        setUpdatingStatus(true);
        setStatusError("");
        try {
            await axiosInstance.patch(`/travel-orders/${id}/status`, {
                status: status,
                sub_status: subStatus,
            });
            setOrder((prev) => ({ ...prev, status: status, sub_status: subStatus }));
            setStatusMenuOpen(false);
        } catch (err) {
            setStatusError("Failed to update status. Please try again.");
        } finally {
            setUpdatingStatus(false);
        }
    };

    const handleSendItinerary = async () => {
        if (isDraftOrder) return;
        setSendingItinerary(true);
        try {
            const response = await axiosInstance.post(`/travel-orders/${id}/send-itinerary`);
            setSnackbar({ open: true, message: response?.data?.message, severity: 'success', });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const detailText = typeof detail === 'string' ? detail : '';
            const notConfigured =
                detailText.toLowerCase().includes('not configured') ||
                err?.response?.status === 400 && detailText.toLowerCase().includes('email');
            setSnackbar({
                open: true,
                message: notConfigured
                    ? (detailText || 'Email is not configured. Please configure email settings first.')
                    : (detailText || 'Failed to send Itinerary email.'),
                severity: 'error',
            });
        } finally {
            setSendingItinerary(false);
        }
    };

    const openPaymentLinkForInvoice = (invoiceId) => {
        if (!invoiceId) {
            setSnackbar({
                open: true,
                message: "No payable invoice found for this booking.",
                severity: "error",
            });
            return;
        }
        const primary = order?.travelers?.find((t) => t.is_primary) || order?.travelers?.[0];
        setPaymentLinkDefaultEmail(primary?.email || order?.email || "");
        setPaymentLinkInvoiceId(invoiceId);
        setPaymentLinkModalOpen(true);
    };

    const handleSendPaymentLink = async () => {
        if (isDraftOrder || !order?.id) return;
        setPreparingPaymentLink(true);
        try {
            const pendingRes = await axiosInstance.get(
                `/payment-gateways/travel-orders/${order.id}/pending-payment`
            );
            openPaymentLinkForInvoice(pendingRes.data?.invoice_id);
            // Refresh schedule in case invoices/PTX were just backfilled
            await loadPaymentScheduleForOrder(order.id);
        } catch (err) {
            setSnackbar({
                open: true,
                message: getApiErrorMessage(
                    err,
                    "Unable to prepare payment link. Save the booking with a payment plan and try again."
                ),
                severity: "error",
            });
        } finally {
            setPreparingPaymentLink(false);
        }
    };

    function StatusOptionRow({ label, active, onClick }) {
        return (
            <MDBox
                onClick={onClick}
                display="flex"
                alignItems="center"
                justifyContent="flex-start"
                gap={1.5}
                px={3}
                py={1.1}
                sx={{
                    cursor: "pointer",
                    bgcolor: active ? "#E3F2FD" : "transparent",
                    "&:hover": { bgcolor: active ? "#E3F2FD" : "#F0F3F8" },
                    transition: "background-color 0.15s ease",
                }}
            >
                <Box
                    component="span"
                    sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        bgcolor: "#0E2348",
                        flexShrink: 0,
                    }}
                />
                <MDTypography variant="button" fontWeight="medium" sx={{ color: "#1A2035" }}>
                    {label}
                </MDTypography>
                {active && <Icon sx={{ fontSize: 16, color: "#1565C8" }}>check</Icon>}
            </MDBox>
        );
    }

    StatusOptionRow.propTypes = {
        label: PropTypes.string.isRequired,
        active: PropTypes.bool,
        onClick: PropTypes.func,
    };

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setIsLoading(true);
            setError("");
            try {
                try {
                    await axiosInstance.post("/travel-orders/check-non-payment-cancellations");
                } catch (e) {
                    // Non-blocking
                }
                const response = await axiosInstance.get(`/travel-orders/${id}/detail`);
                if (!cancelled) setOrder(response.data);
            } catch (err) {
                if (!cancelled) {
                    setOrder(null);
                    setError("Order not found");
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [axiosInstance, id]);

    useEffect(() => {
        if (!order?.id) return;
        loadPaymentScheduleForOrder(order.id);
    }, [order?.id]);

    useEffect(() => {
        if (!order?.id) return;
        let cancelled = false;
        const loadMerchantOrderLines = async () => {
            setLoadingMerchantOrderLines(true);
            try {
                const response = await axiosInstance.get(
                    `/merchant-order-lines/?travel_order_id=${order.id}`
                );
                if (!cancelled) setMerchantOrderLines(response.data || []);
            } catch (err) {
                if (!cancelled) setMerchantOrderLines([]);
            } finally {
                if (!cancelled) setLoadingMerchantOrderLines(false);
            }
        };
        loadMerchantOrderLines();
        return () => {
            cancelled = true;
        };
    }, [axiosInstance, order?.id]);

    const loadOrderCancellationsForOrder = async (travelOrderId) => {
        if (!travelOrderId) return;
        setLoadingOrderCancellations(true);
        try {
            const response = await axiosInstance.get(
                `/order-cancellations/?travel_order_id=${travelOrderId}`
            );
            setOrderCancellations(response.data || []);
        } catch (err) {
            setOrderCancellations([]);
        } finally {
            setLoadingOrderCancellations(false);
        }
    };

    useEffect(() => {
        if (!order?.id) return;
        loadOrderCancellationsForOrder(order.id);
    }, [axiosInstance, order?.id]);

    useEffect(() => {
        if (!order || order.payment?.has_pricing_snapshot) return;   // new orders use stored taxes
        axiosInstance
            .get("/taxes/active")
            .then((res) => setTaxes(res.data || []))
            .catch((err) => console.error("Unable to retrieve taxes", err));
    }, [axiosInstance, order?.id, order?.payment?.has_pricing_snapshot]);

    const handleGenerateMerchantOrderLines = async () => {
        if (!order?.id) return;
        setGeneratingMerchantOrderLines(true);
        try {
            const response = await axiosInstance.post(
                `/merchant-order-lines/generate-for-travel-order/${order.id}`
            );
            const refresh = await axiosInstance.get(
                `/merchant-order-lines/?travel_order_id=${order.id}`
            );
            setMerchantOrderLines(refresh.data || []);
            setSnackbar({
                open: true,
                message: response.data?.message || "Merchant order lines updated.",
                severity: response.data?.success === false ? "warning" : "success",
            });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setSnackbar({
                open: true,
                message:
                    typeof detail === "string"
                        ? detail
                        : "Could not create merchant order lines / send service requests.",
                severity: "error",
            });
        } finally {
            setGeneratingMerchantOrderLines(false);
        }
    };

    const handleResendLineEmail = async (lineId) => {
        setResendingLineId(lineId);
        try {
            await axiosInstance.post(`/merchant-order-lines/${lineId}/resend-request-email`);
            setSnackbar({ open: true, message: "Service request email resent successfully.", severity: "success" });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setSnackbar({
                open: true,
                message: typeof detail === "string" ? detail : "Error resending service request email",
                severity: "error",
            });
        } finally {
            setResendingLineId(null);
        }
    };

    const loadPaymentScheduleForOrder = async (travelOrderId) => {
        setLoadingPaymentSchedule(true);
        try {
            const response = await axiosInstance.get(
                `/payment-gateways/travel-orders/${travelOrderId}/payment-schedule`
            );
            setPaymentSchedule(response.data || []);
        } catch (err) {
            console.error("Error loading payment schedule:", err);
            setPaymentSchedule([]);
        } finally {
            setLoadingPaymentSchedule(false);
        }
    };

    const paymentSummary = useMemo(() => {
        // Use the order's own snapshotted total and actual paid amount.
        // A cancellation scales unpaid schedule items down, but summing the full schedule
        // might include paid rows plus reduced unpaid rows without matching order total.
        const isCancelled = order?.status === "Cancelled" || order?.status === "Failed";
        if (paymentSchedule && paymentSchedule.length > 0) {
            const activeItems = paymentSchedule.filter(
                (item) => (item.payment_status || "").toLowerCase().trim() !== "cancelled"
            );
            const paid = paymentSchedule
                .filter((item) => mapPaymentStatus(item.payment_status) === "paid")
                .reduce((sum, item) => sum + Number(item.amount || 0), 0);
            const pending = isCancelled
                ? 0
                : paymentSchedule
                    .filter((item) => {
                        const s = mapPaymentStatus(item.payment_status);
                        return s !== "paid" && s !== "cancelled";
                    })
                    .reduce((sum, item) => sum + Number(item.amount || 0), 0);
            const scheduleTotal = activeItems.reduce((sum, item) => sum + Number(item.amount || 0), 0);
            const total = isCancelled
                ? Number(order?.payment?.total_amount || 0)
                : (scheduleTotal > 0 ? scheduleTotal : Number(order?.payment?.total_amount || 0));
            return { total, paid, pending };
        }

        const total = Number(order?.payment?.total_amount || 0);
        const paid = Number(order?.payment?.paid_amount || 0);
        const pending = isCancelled ? 0 : Number(order?.payment?.pending_amount ?? Math.max(0, total - paid));
        return { total, paid, pending };
    }, [paymentSchedule, order?.payment, order?.status]);

    const supplementSeqById = useMemo(
        () =>
            Object.fromEntries(
                (order?.payment?.supplements || []).map((s) => [s.invoice_id, s.sequence])
            ),
        [order?.payment?.supplements]
    );

    // ----------- PACKAGE ATTACHMENT -------------------

    const [packageImage, setPackageImage] = useState("");
    const [loadingPackageImage, setLoadingPackageImage] = useState(false);

    useEffect(() => {
        const packageId = order?.trip?.trip_package_id; // <-- adjust to the real field
        if (!packageId) return;
        fetchPackageImage(packageId);
    }, [order?.trip?.trip_package_id]);

    const fetchPackageImage = async (packageId) => {
    setLoadingPackageImage(true);
    try {
        const response = await axiosInstance.get("/attachments/", {
            params: { parent_table: "trip_packages", parent_id: packageId },
        });
        const imageAttachments = (response.data || []).filter(
            (a) => a.file_type && a.file_type.startsWith("image/")
        );
        const heroAttachment = imageAttachments.length
            ? [...imageAttachments].sort((a, b) => b.id - a.id)[0]
            : null;

        if (heroAttachment?.bucket_name && heroAttachment?.storage_path) {
            const url = await previewPrivateObject({ axiosInstance, attachmentId: heroAttachment.id });
            setPackageImage(url || "");
        } else {
            setPackageImage("");
        }
    } catch (err) {
        console.error("Error fetching package attachments:", err);
        setPackageImage("");
    } finally {
        setLoadingPackageImage(false);
    }
};

const handlePreviewTripAttachment = async (attachment) => {
    try {
        if (!attachment.bucket_name || !attachment.storage_path) {
            throw new Error("Attachment storage metadata is invalid for strict R2 download");
        }
        return await previewPrivateObject({ axiosInstance, attachmentId: attachment.id });
    } catch (err) {
        console.error("Error previewing trip attachment:", err);
        return null;
    }
};

useEffect(() => {
    if (!cancellationDialog || !order?.id || !selectedIds.length || !cancellationType) {
        setCancellationPreview(null);
        setCancellationPreviewError("");
        setCancellationPreviewLoading(false);
        return undefined;
    }
    let cancelled = false;
    setCancellationPreviewLoading(true);
    setCancellationPreviewError("");
    const timer = setTimeout(async () => {
        try {
            const response = await axiosInstance.post(
                `/travel-orders/${order.id}/cancel-travelers/preview`,
                {
                    pax_ids: selectedIds,
                    cancellation_type: CANCELLATION_TYPE_MAP[cancellationType],
                }
            );
            if (!cancelled) setCancellationPreview(response.data);
        } catch (err) {
            if (!cancelled) {
                setCancellationPreview(null);
                const detail = err?.response?.data?.detail;
                setCancellationPreviewError(
                    typeof detail === "string" ? detail : "Unable to calculate the refund preview."
                );
            }
        } finally {
            if (!cancelled) setCancellationPreviewLoading(false);
        }
    }, 300);
    return () => {
        cancelled = true;
        clearTimeout(timer);
    };
}, [axiosInstance, cancellationDialog, order?.id, selectedIds, cancellationType]);


// ------------------------------------------



if (isLoading) {
    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3} display="flex" justifyContent="center">
                <PearlLogoLoader size="md" message="Loading order..." />
            </MDBox>
        </DashboardLayout>
    );
}

if (!order) {
    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3} px={3}>
                <MDTypography variant="h6" mb={2}>
                    {error || "Order not found"}
                </MDTypography>
                <MDButton variant="gradient" color="info" onClick={() => navigate("/travel-orders")}>
                    Back to Orders
                </MDButton>
            </MDBox>
        </DashboardLayout>
    );
}

const formatDateTime = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    const datePart = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    const timePart = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
    return `${datePart}, ${timePart}`;
};

const formatDateOnly = (value) => {
    if (!value) return "—";
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const REFUND_OUTCOME_STYLES = {
    Coupon: { color: "#1565C8", bg: "#E3F2FD" },
    "Direct Refund": { color: "#2E7D32", bg: "#E8F5E9" },
    "No Refund": { color: "#616161", bg: "#F0F0F0" },
};

const getPaymentStatusStyle = (paid, total, sub_status, order_status, payment_status) => {
    const isCancelled = order_status === "Cancelled" || payment_status?.toLowerCase() === "cancelled";
    const isFailed = order_status === "Failed" || payment_status?.toLowerCase() === "failed";
    if (isCancelled) {
        return { label: "ORDER CANCELLED", color: "#C62828", bg: "#FDECEA" };
    }
    if (isFailed) {
        return { label: "FAILED", color: "#C62828", bg: "#FDECEA" };
    }
    if (sub_status?.toLowerCase() === 'draft') {
        return { label: sub_status, color: "#616161", bg: "#F0F0F0" };
    } else if (sub_status?.toLowerCase() === 'payment pending') {
        return { label: sub_status, color: "#E68A06", bg: "#FFF3E0" };
    }
    if (!total) return { label: "—", color: "#616161", bg: "#F0F0F0" };
    if (paid <= 0) return { label: "UNPAID", color: "#C62828", bg: "#FDECEA" };
    if (paid >= total) return { label: "PAID", color: "#2E7D32", bg: "#E8F5E9" };
    return { label: "PARTIALLY PAID", color: "#E68A06", bg: "#FFF3E0" };
};

const getInitials = (name) => {
    if (!name) return "—";
    const parts = name.trim().split(/\s+/);
    return parts.length > 1
        ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
        : parts[0].slice(0, 2).toUpperCase();
};

const formatTripDate = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); // e.g. 12 Oct 2026
};

const activeTravelers = (order.travelers || []).filter((t) => !t.is_cancelled);
const remainingTravelers = activeTravelers.filter((t) => !selectedIds.includes(t.id));
const primarySelected = activeTravelers.some((t) => t.is_primary && selectedIds.includes(t.id));
const needsNewPrimary = primarySelected && remainingTravelers.length > 0;
const cancellingWholeOrder = activeTravelers.length > 0 && remainingTravelers.length === 0;

const reloadOrder = async () => {
    try {
        await axiosInstance.post("/travel-orders/check-non-payment-cancellations");
    } catch (e) {
        // Non-blocking
    }
    const response = await axiosInstance.get(`/travel-orders/${id}/detail`);
    setOrder(response.data);
    loadPaymentScheduleForOrder(id);
    loadOrderCancellationsForOrder(id);
    // Reload merchant order lines so the MOL section reflects any status/pax-count
    // changes caused by the traveler cancellation (e.g. MOL Booked → Cancelled, or
    // reduced pax_count when only some travelers on an add-on are cancelled).
    try {
        const molResponse = await axiosInstance.get(`/merchant-order-lines/?travel_order_id=${id}`);
        setMerchantOrderLines(molResponse.data || []);
    } catch {
        // Non-blocking; stale data is preferable to a hard failure
    }
};

const resetCancellationForm = () => {
    setSelectedIds([]);
    setCancellationType(null);
    setNewPrimaryId(null);
    setCancellationComments("");
};

const handleCancellation = async () => {
    if (!selectedIds.length) {
        setSnackbar({ open: true, message: "Select at least one traveler to cancel.", severity: "error" });
        return;
    }
    if (!cancellationType) {
        setSnackbar({ open: true, message: "Please select a cancellation type.", severity: "error" });
        return;
    }
    if (needsNewPrimary && !newPrimaryId) {
        setSnackbar({ open: true, message: "Select a new primary traveler.", severity: "error" });
        return;
    }

    setCancellationInProgress(true);
    try {
        const response = await axiosInstance.post(`/travel-orders/${order.id}/cancel-travelers`, {
            pax_ids: selectedIds,
            cancellation_type: CANCELLATION_TYPE_MAP[cancellationType],
            new_primary_pax_id: needsNewPrimary ? newPrimaryId : null,
            comments: cancellationComments.trim() || null,
        });
        await reloadOrder();
        setSnackbar({
            open: true,
            message: response.data?.order_cancelled
                ? "Booking cancelled successfully."
                : `${selectedIds.length} traveler${selectedIds.length === 1 ? "" : "s"} cancelled successfully.`,
            severity: "success",
        });
        setCancellationDialog(false);
        resetCancellationForm();
    } catch (error) {
        const detail = error?.response?.data?.detail;
        setSnackbar({
            open: true,
            message: typeof detail === "string" ? detail : "Failed to cancel. Please try again.",
            severity: "error",
        });
    } finally {
        setCancellationInProgress(false);
    }
};

const handleCancellationDialogClose = () => {
    setCancellationDialog(false);
    resetCancellationForm();
};

const handleOpenCancellationDialog = () => {
    if (isDraftOrder) return;
    if (NON_CANCELLABLE_STATUSES.includes(order.status)) {
        setSnackbar({
            open: true,
            message: `This order is already ${order.status.toLowerCase()} and cannot be cancelled again.`,
            severity: "error",
        });
        return;
    }
    // Preselect everyone: this keeps "Cancel Booking" meaning the whole booking; untick travelers who stay.
    setSelectedIds(activeTravelers.map((t) => t.id));
    setCancellationDialog(true);
};

const isMainSectionVisible = (label) => visibleMainSections.includes(label);
const mainSectionOrder = (label) => {
    const idx = visibleMainSections.indexOf(label);
    return idx >= 0 ? idx : 0;
};

const bookingLead = order.travelers?.find((tr) => tr.is_primary) || order.travelers?.[0];
const isOrderCancelled = ["cancelled", "cancellation", "failed"].includes(String(order.status || "").trim().toLowerCase());
const isOrderFullyLocked = (() => {
    const s = String(order.status || "").trim().toLowerCase();
    const sub = String(order.sub_status || "").trim().toLowerCase();
    const lockedStatuses = ["cancelled", "cancellation", "failed", "travel in progress", "travel completed"];
    const lockedSubStatuses = [
        "travel in progress",
        "travel completed",
        "cancelled by customer",
        "cancelled by organization",
        "cancelled - seats unavailable (refunded)",
        "failed",
        "payment failed",
    ];
    return lockedStatuses.includes(s) || lockedSubStatuses.includes(sub);
})();
const isDraftOrder =
    String(order.status || "").trim().toLowerCase() === "draft" &&
    ["draft", ""].includes(String(order.sub_status || "").trim().toLowerCase());
const bookingPayment = getPaymentStatusStyle(paymentSummary.paid, paymentSummary.total, order.sub_status, order.status, order.payment_status);
const v2BookingHeader = (
    <>
        <PageHeader
            title={`Booking ${order.order_id || ""}`.trim()}
            summary={
                <>
                    Booked on {formatDate(order.order_date)}
                    {order.created_by ? (
                        <>
                            {" by "}
                            <MDTypography component="span" sx={{ cursor: "pointer", fontSize: "inherit", color: v2t.link }} onClick={() => navigate(`/users/${order.created_by}/profile`)}>
                                {order.created_by_name}
                            </MDTypography>
                        </>
                    ) : null}
                </>
            }
            actions={
                <>
                    <StatusBadge status={order.status} label={order.status} />
                    {!isOrderFullyLocked && (
                        <MDButton variant="gradient" color="info" startIcon={<EditIcon />} onClick={() => navigate(`/travel-orders/${order.id}/edit`)}>
                            Edit
                        </MDButton>
                    )}
                </>
            }
        />
        <Card sx={{ p: 2.5, mb: 3 }}>
            <KeyFieldStrip
                items={[
                    { label: "Lead traveller", value: bookingLead?.name },
                    { label: "Trip", value: order.trip?.trip_name },
                    {
                        label: "Travel dates",
                        value: order.trip?.departure_date ? `${formatTripDate(order.trip.departure_date)} – ${formatTripDate(order.trip.return_date)}` : null,
                    },
                    { label: "Travellers", value: order.travelers?.length ? `${order.travelers.length} · ${formatTravelerMix(order.travelers)}` : null },
                    { label: "Total", value: formatAmount(paymentSummary.total) },
                    { label: "Payment", value: bookingPayment?.label },
                ]}
            />
        </Card>
    </>
);

return (
    <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={isV2 ? 0 : 6} pb={3}>

            {/* HEADER */}

            {isV2 ? v2BookingHeader : (
                <Grid container spacing={3} mb={3}>
                    <Grid item xs={12}>
                        <Card sx={{ borderRadius: "12px", border: "1px solid #E8EDF5", boxShadow: "none" }}>
                            <MDBox
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                flexWrap="wrap"
                                gap={2}
                                p={2.5}
                            >
                                <MDBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
                                    <MDBox display="flex" alignItems="center" gap={1.25}>
                                        <MonoText variant="h6" fontWeight="bold" sx={{ fontSize: "1.15rem" }}>
                                            {order.order_id}
                                        </MonoText>
                                        <StatusBadge status={order.status} label={order.status} />
                                    </MDBox>

                                    <Divider orientation="vertical" flexItem sx={{ height: 28, alignSelf: "center" }} />

                                    <MDBox display="flex">
                                        <MDTypography variant="button" color="text" fontWeight="regular">
                                            Booked on {formatDate(order.order_date)} {order.created_by ? `\u00A0- by\u00A0` : ""}
                                        </MDTypography>
                                        {
                                            order.created_by &&
                                            <MDTypography sx={{ cursor: "pointer" }} color="info" onClick={() => navigate(`/users/${order.created_by}/profile`)} variant="button" fontWeight="regular">{order.created_by_name}</MDTypography>
                                        }
                                    </MDBox>
                                </MDBox>

                                {!isOrderFullyLocked && (
                                    <MDButton
                                        variant="gradient"
                                        color="warning"
                                        startIcon={<EditIcon />}
                                        onClick={() => navigate(`/travel-orders/${order.id}/edit`)}
                                    >
                                        Edit
                                    </MDButton>
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            )}

            <Grid container spacing={3}>

                {!order.payment?.payment_template && !isOrderFullyLocked && (
                    <Grid item xs={12}>
                        <Alert
                            severity="warning"
                            action={
                                <MDButton
                                    variant="outlined"
                                    color="warning"
                                    size="small"
                                    onClick={() => navigate(`/travel-orders/${order.id}/edit`)}
                                >
                                    Select payment plan
                                </MDButton>
                            }
                        >
                            No payment template has been selected for this booking. Select one before sending a payment link to the customer.
                        </Alert>
                    </Grid>
                )}

                {/* Main column */}

                <Grid item xs={12} lg={8}>
                    <Grid container spacing={3}>

                        {/* Main-column sections — order/visibility from page layout (travelorder prefs). */}

                        {isMainSectionVisible("Booking Information") && (
                            <Grid item xs={12} md={6} sx={{ order: mainSectionOrder("Booking Information") }}>
                                <InfoCard
                                    icon="confirmation_number"
                                    title="Booking Information"
                                    headerAction={
                                        <IconButton size="small">
                                            <EditIcon sx={{ fontSize: 18, color: "#5C6B82" }} />
                                        </IconButton>
                                    }
                                >
                                    <Grid container rowSpacing={2.5} columnSpacing={2}>
                                        <DetailField label="Booking ID" value={<MonoText>{order.order_id}</MonoText>} xs={6} md={6} />
                                        <DetailField label="Booking Date" value={formatDateTime(order.order_date)} xs={6} md={6} />

                                        <Grid item xs={6} md={6}>
                                            <MDTypography variant="caption" color="text" fontWeight="medium" display="block" mb={0.5}>
                                                Payment Status
                                            </MDTypography>
                                            {(() => {
                                                const style = getPaymentStatusStyle(
                                                    paymentSummary.paid,
                                                    paymentSummary.total,
                                                    order.sub_status,
                                                    order.status,
                                                    order.payment_status
                                                );
                                                return (
                                                    <Chip
                                                        size="small"
                                                        label={style.label}
                                                        sx={{ bgcolor: style.bg, color: style.color, fontWeight: 700, fontSize: "0.7rem" }}
                                                    />
                                                );
                                            })()}
                                        </Grid>

                                        <Grid item xs={6} md={6}>
                                            <MDTypography variant="caption" color="text" fontWeight="medium" display="block" mb={0.5}>
                                                Total Amount
                                            </MDTypography>
                                            <MDTypography variant="h6" fontWeight="bold" color="dark">
                                                {formatAmount(paymentSummary.total)}
                                            </MDTypography>
                                            {Number(order.payment?.supplement_total || 0) > 0 && (
                                                <MDBox mt={0.5}>
                                                    <SupplementChip label={`+ ${formatAmount(Number(order.payment.supplement_total))} supplements`} />
                                                </MDBox>
                                            )}
                                        </Grid>
                                    </Grid>
                                </InfoCard>
                            </Grid>
                        )}

                        {isMainSectionVisible("Primary Traveler Information") && (
                            <Grid item xs={12} md={6} sx={{ order: mainSectionOrder("Primary Traveler Information") }}>
                                <InfoCard
                                    icon="person"
                                    title="Primary Traveler Information"
                                    headerAction={
                                        <IconButton size="small">
                                            <Icon sx={{ fontSize: 18, color: "#5C6B82" }}>description</Icon>
                                        </IconButton>
                                    }
                                >
                                    {(() => {
                                        const activePax = (order.travelers || []).filter(
                                            (t) => !t.is_cancelled && !t.is_pax_cancelled && t.status !== "Cancelled"
                                        );
                                        const primaryTraveler =
                                            activePax.find((t) => t.is_primary) ||
                                            activePax[0] ||
                                            order.travelers?.find((t) => t.is_primary) ||
                                            order.travelers?.[0];
                                        const { adults: adultsCount, children: childrenCount } = countTravelerTypes(activePax);

                                        return (
                                            <MDBox display="flex" gap={1.5} alignItems="flex-start">
                                                <MDBox
                                                    sx={{
                                                        width: 44,
                                                        height: 44,
                                                        borderRadius: "50%",
                                                        bgcolor: "#E3F2FD",
                                                        color: "#1565C8",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        fontWeight: 700,
                                                        fontSize: "0.9rem",
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {getInitials(primaryTraveler?.name)}
                                                </MDBox>

                                                <MDBox flex={1} minWidth={0}>
                                                    <MDTypography variant="button" fontWeight="bold" display="block">
                                                        {primaryTraveler?.name || "—"}
                                                    </MDTypography>

                                                    <MDBox display="flex" alignItems="center" gap={0.5} mt={0.5}>
                                                        <Icon sx={{ fontSize: 15, color: "text.secondary" }}>call</Icon>
                                                        <MDTypography variant="caption" color="text">
                                                            {primaryTraveler?.dial_code} {primaryTraveler?.mobile || order.contact_number || "—"}
                                                        </MDTypography>
                                                    </MDBox>

                                                    <MDBox display="flex" alignItems="center" gap={0.5} mt={0.25}>
                                                        <Icon sx={{ fontSize: 15, color: "text.secondary" }}>mail</Icon>
                                                        <MDTypography variant="caption" color="text">
                                                            {order.email || "—"}
                                                        </MDTypography>
                                                    </MDBox>

                                                    <MDTypography variant="caption" color="info" fontWeight="bold" display="block" mt={0.75}>
                                                        {adultsCount} Adult{adultsCount !== 1 ? "s" : ""}
                                                        {childrenCount > 0 ? `, ${childrenCount} Child${childrenCount !== 1 ? "ren" : ""}` : ""}
                                                    </MDTypography>
                                                </MDBox>
                                            </MDBox>
                                        );
                                    })()}
                                </InfoCard>
                            </Grid>
                        )}

                        {isMainSectionVisible("Trip Package Information") && (
                            <Grid item xs={12} sx={{ order: mainSectionOrder("Trip Package Information") }}>
                                <InfoCard icon="near_me" title="Trip Package Information">

                                    <MDBox display="flex" gap={2} alignItems="flex-start">

                                        {/* Left: trip image */}

                                        <Box
                                            sx={{
                                                borderRadius: 1,
                                                overflow: "hidden",
                                                minHeight: 50,
                                                width: 76,
                                                height: 60,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flexShrink: 0,
                                            }}
                                        >
                                            {loadingPackageImage ? (
                                                <CircularProgress size={24} />
                                            ) : (packageImage) ? (
                                                <MDBox
                                                    component="img"
                                                    src={packageImage}
                                                    alt={order.trip.trip_name || "Trip"}
                                                    sx={{ width: 72, borderRadius: 1, height: 56, objectFit: "cover", bgcolor: "#F0F3F8" }}
                                                />
                                            ) : (
                                                <Icon sx={{ fontSize: 36, color: "#B0BAC9" }}>image</Icon>
                                            )}
                                        </Box>

                                        {/* Right: existing trip package info + flight summary */}

                                        <MDBox flex={1} minWidth={0}>
                                            <MDBox display="flex" gap={4}>
                                                <MDBox>
                                                    <MDTypography variant="caption" color="text" fontWeight="medium" display="block" mb={0.5}>
                                                        Trip Package Name
                                                    </MDTypography>
                                                    <MDTypography
                                                        variant="button"
                                                        fontWeight="bold"
                                                        color="info"
                                                        display="block"
                                                        // onClick={() => navigate(`/trips/detail/${order.trip.id}`)}
                                                        onClick={() => navigate(`/trip-packages/detail/${order.trip.trip_package_id}`)}
                                                        sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                                                    >
                                                        {order.trip.trip_name || "—"}
                                                    </MDTypography>
                                                </MDBox>

                                                <MDBox sx={{ flexShrink: 0 }}>
                                                    <MDTypography variant="caption" color="text" fontWeight="medium" display="block" mb={0.5}>
                                                        Travel Dates
                                                    </MDTypography>

                                                    <MDTypography
                                                        variant="button"
                                                        fontWeight="bold"
                                                        color="dark"
                                                        display="block"
                                                        sx={{ whiteSpace: "nowrap" }}
                                                    >
                                                        {formatTripDate(order.trip.departure_date)} – {formatTripDate(order.trip.return_date)}
                                                    </MDTypography>

                                                    <MDTypography variant="caption" color="text" display="block">
                                                        {order.trip.duration || "—"}
                                                    </MDTypography>
                                                </MDBox>

                                                {order.trip.type && (
                                                    <MDBox>
                                                        <MDTypography variant="caption" color="text" fontWeight="medium" display="block" mb={0.5}>
                                                            Type
                                                        </MDTypography>
                                                        <Chip
                                                            size="small"
                                                            label={order.trip.type}
                                                            sx={{ bgcolor: "#FFF3E0", color: "#B26A00", fontWeight: 700, fontSize: "0.7rem" }}
                                                        />
                                                    </MDBox>
                                                )}
                                            </MDBox>

                                            {(() => {
                                                const flightItems = (order.transportation || []).filter(
                                                    (item) => (item.type || "").toLowerCase() === "flight"
                                                );
                                                if (flightItems.length === 0) return null;

                                                return (
                                                    <MDBox display="flex" flexDirection="column" gap={1}>
                                                        {flightItems.map((item) => (
                                                            <MDBox
                                                                key={item.id}
                                                                display="flex"
                                                                alignItems="center"
                                                                justifyContent="space-between"
                                                                gap={2}
                                                                p={1.5}
                                                                borderRadius="lg"
                                                                sx={{ border: "1px solid #E8EDF5", bgcolor: "#FAFBFD" }}
                                                            >
                                                                <MDBox display="flex" alignItems="center" gap={1.5}>
                                                                    <MDBox
                                                                        sx={{
                                                                            width: 36,
                                                                            height: 36,
                                                                            borderRadius: "50%",
                                                                            bgcolor: "#E3F2FD",
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            justifyContent: "center",
                                                                            flexShrink: 0,
                                                                        }}
                                                                    >
                                                                        <FlightIcon sx={{ color: "#1565C8", fontSize: 18 }} />
                                                                    </MDBox>
                                                                    <MDBox>
                                                                        <MDBox>
                                                                            <MDBox display="flex" alignItems="center" gap={0.75}>
                                                                                <MDTypography variant="button" fontWeight="bold" display="block">
                                                                                    {item.name}
                                                                                </MDTypography>
                                                                                {item.is_supplement && <SupplementChip />}
                                                                            </MDBox>
                                                                            {item.description && (
                                                                                <MDTypography variant="caption" color="text">
                                                                                    {item.description}
                                                                                </MDTypography>
                                                                            )}
                                                                        </MDBox>
                                                                        {item.description && (
                                                                            <MDTypography variant="caption" color="text">
                                                                                {item.description}
                                                                            </MDTypography>
                                                                        )}
                                                                    </MDBox>
                                                                </MDBox>
                                                                <MDButton
                                                                    variant="outlined"
                                                                    color="info"
                                                                    size="small"
                                                                    onClick={() => {
                                                                        document.getElementById("transportation-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
                                                                    }}
                                                                >
                                                                    View Details
                                                                </MDButton>
                                                            </MDBox>
                                                        ))}
                                                    </MDBox>
                                                );
                                            })()}
                                        </MDBox>

                                    </MDBox>

                                </InfoCard>
                            </Grid>
                        )}

                        {isMainSectionVisible("Travelers Information") && (
                            <Grid item xs={12} sx={{ order: mainSectionOrder("Travelers Information") }}>
                                <InfoCard
                                    icon="groups"
                                    title="Travelers Information"
                                    headerAction={
                                        <MDTypography variant="caption" color="text" fontWeight="medium">
                                            {order.travelers?.length || 0} Traveler{order.travelers?.length === 1 ? "" : "s"}
                                            {order.travelers?.length ? ` · ${formatTravelerMix(order.travelers)}` : ""}
                                        </MDTypography>
                                    }
                                >
                                    <TravelersTable travelers={order.travelers} />
                                </InfoCard>
                            </Grid>
                        )}

                        {isMainSectionVisible("Add-on Services & Pricing Details") && (
                            <Grid item xs={12} sx={{ order: mainSectionOrder("Add-on Services & Pricing Details") }}>
                                <InfoCard icon="add_circle" title="Add-on Services & Pricing Details">
                                    {(() => {

                                        const p = order.payment || {};
                                        const isSnapshot = Boolean(p.has_pricing_snapshot);
                                        const n = (v) => Number(v || 0);
                                        const accommodations = p.accommodations || [];
                                        const addons = p.addons || [];

                                        // ---------- NEW orders (server snapshot) ----------
                                        const paxCount = p.pax_count || 0;

                                        const hssParts = [];
                                        if (n(p.hss_percentage) > 0) hssParts.push(`${n(p.hss_percentage)}%`);
                                        if (n(p.hss_flat_charge) > 0) hssParts.push(formatAmount(n(p.hss_flat_charge)));
                                        const hssLabel = `High Season Surcharge${hssParts.length ? ` (${hssParts.join(" + ")})` : ""}`;

                                        const DISCOUNT_LABELS = { early_bird: "Early Bird Discount", last_minute: "Last Minute Discount", simple: "Discount" };
                                        const discountLabel = `${DISCOUNT_LABELS[p.discount_type] || "Discount"} (${n(p.discount_percentage)}%)`;

                                        const travelersSubtotal = n(p.subtotal_per_pax) * paxCount;
                                        const taxTotal = n(p.tax_total);

                                        // ---------- OLD orders (original client-side calculation) ----------
                                        const legacyBasePrice = n(p.base_price);
                                        const legacyPax = order.travelers?.length || 1;

                                        const legacyLineTotal = (amount, nights, paxCount_, pricingBasis) => {
                                            const hasNights = nights != null && nights > 0;
                                            const hasPax = paxCount_ != null && paxCount_ > 0;
                                            const basis = (pricingBasis || "per_passenger").toLowerCase();
                                            if (basis === "fixed") return n(amount) * (hasNights ? nights : 1);
                                            return n(amount) * (hasNights ? nights : 1) * (hasPax ? paxCount_ : 1);
                                        };
                                        const legacyPreTax =
                                            legacyLineTotal(legacyBasePrice, null, legacyPax) +
                                            accommodations.filter((a) => !a.is_cancelled).reduce((s, a) => s + legacyLineTotal(a.amount, a.nights, a.pax_count), 0) +
                                            addons.filter((a) => !a.is_cancelled).reduce((s, a) => s + legacyLineTotal(a.amount, null, a.pax_count, a.pricing_basis), 0);

                                        // ---------- shared ----------
                                        const preTax = isSnapshot ? n(p.total_amount) - taxTotal : legacyPreTax;

                                        const totalAmount = paymentSummary.total;
                                        const paidAmount = paymentSummary.paid;
                                        const pendingAmount = paymentSummary.pending;
                                        const paidPct = totalAmount > 0 ? Math.min(100, Math.round((paidAmount / totalAmount) * 100)) : 0;

                                        const visibleAddons = addons.filter(
                                            (addon) => !addon.is_cancelled || (n(addon.amount) > 0 && ((addon.pricing_basis || "per_passenger") === "fixed" || (addon.pax_count || 0) > 0))
                                        );

                                        const renderServiceCategory = (title) => (
                                            <MDBox display="flex" alignItems="center" gap={1} mt={1} mb={0.25}>
                                                <MDTypography
                                                    variant="caption"
                                                    fontWeight="bold"
                                                    color="secondary"
                                                    sx={{ textTransform: "uppercase", fontSize: "0.68rem", letterSpacing: "0.5px" }}
                                                >
                                                    {title}
                                                </MDTypography>
                                                <Divider sx={{ flexGrow: 1, my: 0 }} />
                                            </MDBox>
                                        );

                                        return (
                                            <Grid container spacing={3}>
                                                {/* Left: itemized breakdown */}
                                                <Grid item xs={12} md={6}>
                                                    <MDTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase" display="block" mb={1.5}>
                                                        Included Services
                                                    </MDTypography>

                                                    <MDBox
                                                        sx={{
                                                            bgcolor: "#FAFBFD",
                                                            border: "1px solid #E8EDF5",
                                                            borderRadius: 1,
                                                            p: 2,
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            gap: 0.5,
                                                        }}
                                                    >
                                                        {/* Base package items */}
                                                        {isSnapshot ? (
                                                            <>
                                                                <LineItem
                                                                    icon="receipt_long"
                                                                    label="Trip Base Price"
                                                                    amount={n(p.base_price_per_pax)}
                                                                    paxCount={paxCount}
                                                                    bold
                                                                    formatAmount={formatAmount}
                                                                    iconBg="#EBF3FB"
                                                                    iconColor="#1565C8"
                                                                />

                                                                {n(p.hss_per_pax) > 0 && (
                                                                    <LineItem
                                                                        icon="trending_up"
                                                                        label={hssLabel}
                                                                        amount={n(p.hss_per_pax)}
                                                                        paxCount={paxCount}
                                                                        formatAmount={formatAmount}
                                                                        iconBg="#FFF4E5"
                                                                        iconColor="#ED6C02"
                                                                    />
                                                                )}
                                                                {n(p.discount_per_pax) > 0 && (
                                                                    <LineItem
                                                                        icon="sell"
                                                                        label={discountLabel}
                                                                        amount={-n(p.discount_per_pax)}
                                                                        paxCount={paxCount}
                                                                        formatAmount={formatAmount}
                                                                        iconBg="#EDF7ED"
                                                                        iconColor="#2E7D32"
                                                                    />
                                                                )}
                                                            </>
                                                        ) : (
                                                            <LineItem
                                                                icon="receipt_long"
                                                                label="Trip Base Price"
                                                                amount={legacyBasePrice}
                                                                paxCount={legacyPax}
                                                                bold
                                                                formatAmount={formatAmount}
                                                                iconBg="#EBF3FB"
                                                                iconColor="#1565C8"
                                                            />
                                                        )}

                                                        {/* Accommodations */}
                                                        {accommodations.length > 0 && (
                                                            <>
                                                                {renderServiceCategory("Accommodations")}
                                                                {accommodations.map((acc, i) => (
                                                                    <LineItem
                                                                        key={`acc-${i}`}
                                                                        icon="hotel"
                                                                        label={acc.name}
                                                                        amount={n(acc.amount)}
                                                                        nights={isSnapshot ? null : acc.nights}
                                                                        paxCount={(acc.pricing_basis || "per_passenger") === "fixed" ? null : acc.pax_count}
                                                                        cancelled={acc.is_cancelled}
                                                                        formatAmount={formatAmount}
                                                                        iconBg="#F3E8FF"
                                                                        iconColor="#7E22CE"
                                                                    />
                                                                ))}
                                                            </>
                                                        )}

                                                        {/* Add-on Activities */}
                                                        {visibleAddons.length > 0 && (
                                                            <>
                                                                {renderServiceCategory("Add-on Activities")}
                                                                {visibleAddons.map((addon, i) => (
                                                                    <LineItem
                                                                        key={`addon-${addon.id || addon.name || i}-${i}`}
                                                                        icon="add_circle_outline"
                                                                        label={addon.name}
                                                                        amount={n(addon.amount)}
                                                                        paxCount={(addon.pricing_basis || "per_passenger") === "fixed" ? null : addon.pax_count}
                                                                        cancelled={addon.is_cancelled}
                                                                        formatAmount={formatAmount}
                                                                        iconBg="#E0F2FE"
                                                                        iconColor="#0284C7"
                                                                    />
                                                                ))}
                                                            </>
                                                        )}

                                                        {accommodations.length === 0 && visibleAddons.length === 0 && (
                                                            <MDBox py={0.75} textAlign="center">
                                                                <MDTypography variant="caption" color="text" sx={{ fontStyle: "italic", opacity: 0.7 }}>
                                                                    No add-on services or accommodations selected
                                                                </MDTypography>
                                                            </MDBox>
                                                        )}

                                                        {/* Taxes */}
                                                        {isSnapshot ? (
                                                            (p.tax_lines || []).length > 0 && (
                                                                <>
                                                                    {renderServiceCategory("Taxes & Fees")}
                                                                    {(p.tax_lines || []).map((tax) => (
                                                                        <LineItem
                                                                            key={tax.tax_id ?? tax.type}
                                                                            icon="request_quote"
                                                                            label={tax.type}
                                                                            subtitle={`${n(tax.rate)}% of ${formatAmount(preTax)}`}
                                                                            amount={n(tax.amount)}
                                                                            formatAmount={formatAmount}
                                                                            iconBg="#F1F5F9"
                                                                            iconColor="#64748B"
                                                                        />
                                                                    ))}
                                                                </>
                                                            )
                                                        ) : (
                                                            taxes.length > 0 && (
                                                                <>
                                                                    {renderServiceCategory("Taxes & Fees")}
                                                                    {taxes.map((tax) => {
                                                                        const taxAmount = Number((preTax * (tax.rate / 100)).toFixed(2));
                                                                        return (
                                                                            <LineItem
                                                                                key={tax.id ?? tax.tax_type}
                                                                                icon="request_quote"
                                                                                label={tax.tax_type}
                                                                                subtitle={`${tax.rate}% of ${formatAmount(preTax)}`}
                                                                                amount={taxAmount}
                                                                                formatAmount={formatAmount}
                                                                                iconBg="#F1F5F9"
                                                                                iconColor="#64748B"
                                                                            />
                                                                        );
                                                                    })}
                                                                </>
                                                            )
                                                        )}
                                                    </MDBox>
                                                </Grid>

                                                {/* Right: price summary + payment progress */}
                                                <Grid item xs={12} md={6}>
                                                    <MDTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase" display="block" mb={1.5}>
                                                        Price Summary
                                                    </MDTypography>

                                                    <MDBox sx={{ bgcolor: "#FAFBFD", border: "1px solid #E8EDF5", borderRadius: 1, p: 2 }}>

                                                        {p.has_pricing_snapshot ? (
                                                            <>
                                                                <SummaryLine label={`Travelers (${paxCount} × ${formatAmount(n(p.subtotal_per_pax))})`} value={formatAmount(travelersSubtotal)} />
                                                                {n(p.addons_total) > 0 && <SummaryLine label="Add-ons" value={formatAmount(n(p.addons_total))} />}
                                                                <SummaryLine label="Subtotal (pre-tax)" value={formatAmount(preTax)} />
                                                                {taxTotal > 0 && <SummaryLine label="Taxes" value={formatAmount(taxTotal)} />}
                                                                <Divider sx={{ my: 0.5 }} />
                                                            </>
                                                        ) : null}
                                                        <SummaryLine label="Total Price" value={formatAmount(totalAmount)} bold />

                                                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={2.5} mb={0.5}>
                                                            <MDTypography variant="caption" color="text">Payment Progress</MDTypography>
                                                            {isOrderCancelled ? (
                                                                <Chip
                                                                    size="small"
                                                                    label={order.status === "Failed" ? "Payment Failed" : "Order Cancelled"}
                                                                    sx={{ bgcolor: "#FDECEA", color: "#C62828", fontWeight: 700, fontSize: "0.65rem", height: 20 }}
                                                                />
                                                            ) : (
                                                                <MDTypography variant="caption" fontWeight="bold" color="info">{paidPct}%</MDTypography>
                                                            )}
                                                        </MDBox>
                                                        <MDBox sx={{ height: 8, borderRadius: 4, bgcolor: "#E8EDF5", overflow: "hidden", mb: 1.5 }}>
                                                            <MDBox
                                                                sx={{
                                                                    height: "100%",
                                                                    width: isOrderCancelled ? "100%" : `${paidPct}%`,
                                                                    bgcolor: isOrderCancelled
                                                                        ? "#D32F2F"
                                                                        : (pendingAmount <= 0 ? "#2E7D32" : "#1565C8"),
                                                                    backgroundImage: isOrderCancelled
                                                                        ? "repeating-linear-gradient(45deg, #FFCDD2, #FFCDD2 8px, #EF9A9A 8px, #EF9A9A 16px)"
                                                                        : "none",
                                                                    borderRadius: 4,
                                                                    transition: "width 0.3s ease",
                                                                }}
                                                            />
                                                        </MDBox>

                                                        <MDBox display="flex" justifyContent="space-between" alignItems="center" py={0.5}>
                                                            <MDBox display="flex" alignItems="center" gap={0.75}>
                                                                <MDBox sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#2E7D32" }} />
                                                                <MDTypography variant="caption" color="text">Paid Amount</MDTypography>
                                                            </MDBox>
                                                            <MDTypography variant="button" fontWeight="bold" sx={{ color: "#2E7D32" }}>
                                                                {formatAmount(paidAmount)}
                                                            </MDTypography>
                                                        </MDBox>

                                                        {!isOrderCancelled && pendingAmount > 0 && (
                                                            <MDBox display="flex" justifyContent="space-between" alignItems="center" py={0.5}>
                                                                <MDBox display="flex" alignItems="center" gap={0.75}>
                                                                    <MDBox sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#E68A06" }} />
                                                                    <MDTypography variant="caption" color="text">Pending Amount</MDTypography>
                                                                </MDBox>
                                                                <MDTypography variant="button" fontWeight="bold" sx={{ color: "#E68A06" }}>
                                                                    {formatAmount(pendingAmount)}
                                                                </MDTypography>
                                                            </MDBox>
                                                        )}

                                                        {order.payment?.payment_template && (
                                                            <MDBox mt={2}>
                                                                <Divider sx={{ mb: 1.5 }} />
                                                                <MDBox gap={1} display="flex" alignItems="flex-start" flexDirection="column" justifyContent="space-between" mb={1}>
                                                                    <MDTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">
                                                                        Payment Plan: {order.payment.payment_template.template_name}
                                                                    </MDTypography>
                                                                    <Chip
                                                                        size="small"
                                                                        label={
                                                                            order.payment.payment_template.payment_type === "deposit_installments"
                                                                                ? "Deposit + Installments"
                                                                                : order.payment.payment_template.payment_type === "deposit_final_balance"
                                                                                    ? "Deposit + Final Balance"
                                                                                    : "Full Payment"
                                                                        }
                                                                        sx={{ bgcolor: "#E3F2FD", color: "#1565C8", fontWeight: 700, fontSize: "0.65rem" }}
                                                                    />
                                                                </MDBox>

                                                                {order.payment.payment_template.payment_type === "deposit_final_balance" && (
                                                                    <MDTypography variant="caption" color="text" display="block" mb={1}>
                                                                        {order.payment.payment_template.deposit_percentage}% deposit now, balance due{" "}
                                                                        {order.payment.payment_template.due_days_before_departure} days before departure
                                                                    </MDTypography>
                                                                )}

                                                                {order.payment.payment_template.payment_type === "deposit_installments" && (
                                                                    <MDTypography variant="caption" color="text" display="block" mb={1}>
                                                                        {order.payment.payment_template.deposit_percentage}% deposit now, remaining split across{" "}
                                                                        {order.payment.payment_template.installments.length} installment
                                                                        {order.payment.payment_template.installments.length !== 1 ? "s" : ""}
                                                                    </MDTypography>
                                                                )}

                                                                {/*{paymentSchedule.map((row) => (*/}
                                                                {/*    <PaymentScheduleRow*/}
                                                                {/*        key={row.payment_transaction_id}*/}
                                                                {/*        label={row.installment_number ? `Installment ${row.installment_number}` : row.payment_mode}*/}
                                                                {/*        amount={Number(row.amount)}*/}
                                                                {/*        dueDate={row.payment_due_date}*/}
                                                                {/*        status={mapPaymentStatus(row.payment_status)}*/}
                                                                {/*        formatAmount={formatAmount} */}
                                                                {/*    />*/}
                                                                {/*))}*/}

                                                                {loadingPaymentSchedule && (
                                                                    <MDBox display="flex" justifyContent="center" py={1}>
                                                                        <CircularProgress size={18} />
                                                                    </MDBox>
                                                                )}

                                                                {!loadingPaymentSchedule && paymentSchedule.length === 0 && (
                                                                    <MDTypography variant="caption" color="text" sx={{ fontStyle: "italic", opacity: 0.7 }}>
                                                                        Schedule details unavailable. Use Send Payment Link to create invoices if needed.
                                                                    </MDTypography>
                                                                )}

                                                                {!loadingPaymentSchedule && paymentSchedule.length > 0 && (
                                                                    <PaymentScheduleList
                                                                        rows={paymentSchedule}
                                                                        showInvoiceNumber={paymentSchedule.length > 1}
                                                                        onResendPaymentEmail={isOrderCancelled ? undefined : (row) => openPaymentLinkForInvoice(row.invoice_id)}
                                                                        onInvoiceClick={(row) => navigate(`/invoices/${row.invoice_id}`)}
                                                                    />
                                                                )}
                                                            </MDBox>
                                                        )}

                                                    </MDBox>
                                                </Grid>
                                            </Grid>
                                        );
                                    })()}
                                </InfoCard>
                            </Grid>
                        )}

                        {isMainSectionVisible("Order Cancellation") && orderCancellations.length > 0 && (
                            <Grid item xs={12} sx={{ order: mainSectionOrder("Order Cancellation") }}>
                                <InfoCard
                                    icon="cancel"
                                    title="Order Cancellation"
                                    headerAction={
                                        <MDTypography variant="caption" color="text" fontWeight="medium">
                                            {orderCancellations.length} Record{orderCancellations.length === 1 ? "" : "s"}
                                        </MDTypography>
                                    }
                                >
                                    <MDBox display="flex" flexDirection="column" gap={2}>
                                        {orderCancellations.map((cancellation, index) => {
                                            const termLabel = cancellation.payment_term?.name || (cancellation.payment_term?.amount_percent != null ? `${cancellation.payment_term.amount_percent}% Term` : (cancellation.payment_term_id ? `Term ${cancellation.payment_term_id}` : "Cancellation Policy"));
                                            const termId = cancellation.payment_term_id || cancellation.payment_term?.id;
                                            const paxDisplay = cancellation.order_pax?.passenger_name || cancellation.pax_name || (cancellation.order_pax_id ? `Pax ${cancellation.order_pax_id}` : null);

                                            return (
                                                <MDBox
                                                    key={cancellation.id}
                                                    sx={{
                                                        bgcolor: "#FAFBFD",
                                                        border: "1px solid #E8EDF5",
                                                        borderRadius: 1,
                                                        p: 2,
                                                    }}
                                                >
                                                    {/* Cancellation Item Header */}
                                                    <MDBox
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="space-between"
                                                        pb={1}
                                                        mb={1.5}
                                                        sx={{ borderBottom: "1px solid #E8EDF5" }}
                                                    >
                                                        <MDBox
                                                            display="inline-flex"
                                                            alignItems="center"
                                                            gap={1}
                                                            sx={{ cursor: "pointer" }}
                                                            onClick={() => navigate(`/order-cancellation/${cancellation.id}`)}
                                                        >
                                                            <MDBox
                                                                sx={{
                                                                    width: 22,
                                                                    height: 22,
                                                                    borderRadius: "50%",
                                                                    bgcolor: "#D6E3FF",
                                                                    color: "#1565C8",
                                                                    display: "inline-flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    fontSize: "0.75rem",
                                                                    fontWeight: "bold",
                                                                    lineHeight: 1,
                                                                    flexShrink: 0,
                                                                }}
                                                            >
                                                                {index + 1}
                                                            </MDBox>
                                                            <MDTypography
                                                                component="span"
                                                                variant="button"
                                                                color="info"
                                                                fontWeight="bold"
                                                                sx={{ "&:hover": { textDecoration: "underline" } }}
                                                            >
                                                                {cancellation.name || `CN-${cancellation.id}`}
                                                            </MDTypography>
                                                        </MDBox>
                                                    </MDBox>

                                                    {/* Detail Grid - Same style as Price Summary */}
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12} md={6}>
                                                            <SummaryLine label="Cancellation Type" value={cancellation.cancellation_type || "Customer Cancellation"} />
                                                            <SummaryLine
                                                                label="Cancellation Date"
                                                                value={formatDate(cancellation.requested_on || cancellation.created_at) || "-"}
                                                            />
                                                            <SummaryLine
                                                                label="Payment Terms Applied"
                                                                value={
                                                                    termId ? (
                                                                        <MDTypography
                                                                            component="span"
                                                                            variant="button"
                                                                            color="info"
                                                                            fontWeight="medium"
                                                                            sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                                                                            onClick={() => navigate(`/payment-term/${termId}`)}
                                                                        >
                                                                            {termLabel}
                                                                        </MDTypography>
                                                                    ) : (
                                                                        termLabel
                                                                    )
                                                                }
                                                            />
                                                            <SummaryLine
                                                                label="Cancellation Status"
                                                                value={
                                                                    <Chip
                                                                        size="small"
                                                                        label={cancellation.status || "Active"}
                                                                        color={cancellation.status === "Active" ? "success" : "default"}
                                                                        sx={{ fontWeight: 600, fontSize: "0.65rem", height: 20 }}
                                                                    />
                                                                }
                                                            />
                                                            {paxDisplay && (
                                                                <SummaryLine
                                                                    label="Primary Pax"
                                                                    value={
                                                                        cancellation.order_pax_id ? (
                                                                            <MDTypography
                                                                                component="span"
                                                                                variant="button"
                                                                                color="info"
                                                                                fontWeight="medium"
                                                                                sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                                                                                onClick={() => navigate(`/travel-order-pax/${cancellation.order_pax_id}`)}
                                                                            >
                                                                                {paxDisplay}
                                                                            </MDTypography>
                                                                        ) : (
                                                                            paxDisplay
                                                                        )
                                                                    }
                                                                />
                                                            )}
                                                            {cancellation.comments && (
                                                                <SummaryLine
                                                                    label="Comments / Reason"
                                                                    value={cancellation.comments}
                                                                />
                                                            )}
                                                        </Grid>

                                                        <Grid item xs={12} md={6}>
                                                            <SummaryLine
                                                                label="Total Amount"
                                                                value={formatAmount(cancellation.total_amount != null ? Number(cancellation.total_amount) : 0, cancellation.currency || orderCurrency)}
                                                            />
                                                            <SummaryLine
                                                                label="Amount Deduction"
                                                                value={
                                                                    <MDTypography variant="button" fontWeight="medium" sx={{ color: "#D32F2F" }}>
                                                                        -{formatAmount(cancellation.amount_deduction != null ? Number(cancellation.amount_deduction) : 0, cancellation.currency || orderCurrency)}
                                                                    </MDTypography>
                                                                }
                                                            />
                                                            <Divider sx={{ my: 0.5 }} />
                                                            <SummaryLine
                                                                label="Refund Amount"
                                                                value={
                                                                    <MDTypography variant="button" fontWeight="bold" sx={{ color: "#2E7D32" }}>
                                                                        {formatAmount(cancellation.refund_amount != null ? Number(cancellation.refund_amount) : 0, cancellation.currency || orderCurrency)}
                                                                    </MDTypography>
                                                                }
                                                                bold
                                                            />
                                                            <SummaryLine
                                                                label="Refund Status"
                                                                value={
                                                                    <Chip
                                                                        size="small"
                                                                        label={cancellation.refund_status || "NA"}
                                                                        color={cancellation.refund_status === "Refunded" ? "success" : "info"}
                                                                        sx={{ fontWeight: 600, fontSize: "0.65rem", height: 20 }}
                                                                    />
                                                                }
                                                            />
                                                            {cancellation.coupon_id ? (
                                                                <SummaryLine
                                                                    label="Coupon Reference"
                                                                    value={
                                                                        <MDTypography
                                                                            component="span"
                                                                            variant="button"
                                                                            color="info"
                                                                            fontWeight="medium"
                                                                            sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                                                                            onClick={() => navigate(`/coupons/${cancellation.coupon_id}`)}
                                                                        >
                                                                            {cancellation.coupon?.code || cancellation.coupon?.name || `Coupon ${cancellation.coupon_id}`}
                                                                        </MDTypography>
                                                                    }
                                                                />
                                                            ) : cancellation.invoice_id ? (
                                                                <SummaryLine
                                                                    label="Invoice Reference"
                                                                    value={
                                                                        <MDTypography
                                                                            component="span"
                                                                            variant="button"
                                                                            color="info"
                                                                            fontWeight="medium"
                                                                            sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                                                                            onClick={() => navigate(`/invoices/${cancellation.invoice_id}`)}
                                                                        >
                                                                            {cancellation.invoice?.invoice_number || `Invoice ${cancellation.invoice_id}`}
                                                                        </MDTypography>
                                                                    }
                                                                />
                                                            ) : null}
                                                        </Grid>
                                                    </Grid>
                                                </MDBox>
                                            );
                                        })}
                                    </MDBox>
                                </InfoCard>
                            </Grid>
                        )}

                        {isMainSectionVisible("Merchant Order Lines") && (
                            <Grid item xs={12} sx={{ order: mainSectionOrder("Merchant Order Lines") }}>
                                <InfoCard
                                    icon="storefront"
                                    title="Merchant Order Lines"
                                    headerAction={
                                        <MDTypography variant="caption" color="text" fontWeight="medium">
                                            {merchantOrderLines.length} Line{merchantOrderLines.length === 1 ? "" : "s"}
                                        </MDTypography>
                                    }
                                >
                                    {loadingMerchantOrderLines ? (
                                        <MDBox display="flex" justifyContent="center" py={2}>
                                            <CircularProgress size={22} />
                                        </MDBox>
                                    ) : merchantOrderLines.length === 0 ? (
                                        <MDBox display="flex" flexDirection="column" alignItems="flex-start" gap={1.5}>
                                            <MDTypography variant="caption" color="text" sx={{ fontStyle: "italic", opacity: 0.7 }}>
                                                No merchant order lines for this booking yet. Service request emails cannot be
                                                resent until lines are created (usually after Deposit / Full Payment).
                                            </MDTypography>
                                            <MDButton
                                                variant="outlined"
                                                color="info"
                                                size="small"
                                                onClick={handleGenerateMerchantOrderLines}
                                                disabled={generatingMerchantOrderLines}
                                                startIcon={
                                                    generatingMerchantOrderLines ? (
                                                        <CircularProgress size={14} color="inherit" />
                                                    ) : (
                                                        <EmailOutlinedIcon sx={{ fontSize: 16 }} />
                                                    )
                                                }
                                            >
                                                {generatingMerchantOrderLines
                                                    ? "Creating…"
                                                    : "Create lines & send service requests"}
                                            </MDButton>
                                        </MDBox>
                                    ) : (
                                        <MDBox display="flex" flexDirection="column" gap={1.5}>
                                            {merchantOrderLines.map((line) => {
                                                const chipProps = getLineStatusChipProps(line.status);

                                                return (
                                                    <MDBox
                                                        key={line.id}
                                                        display="flex"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                        gap={2}
                                                        sx={{ cursor: "pointer" }}
                                                        onClick={() => navigate(`/merchant-order-line/${line.id}`)}
                                                    >
                                                        {/* 1st child: line info */}
                                                        <MDBox minWidth={0} flex={2}>
                                                            <MDBox display="flex" alignItems="center" gap={0.75} minWidth={0}>
                                                                <MDTypography variant="button" fontWeight="medium" color="info" noWrap>
                                                                    {line.name || `MOL ${line.id}`}
                                                                </MDTypography>
                                                                {line.supplement_invoice_id && (
                                                                    <SupplementChip
                                                                        label={
                                                                            supplementSeqById[line.supplement_invoice_id]
                                                                                ? `Supplement ${supplementSeqById[line.supplement_invoice_id]}`
                                                                                : "Supplement"
                                                                        }
                                                                    />
                                                                )}
                                                            </MDBox>
                                                            <MDTypography variant="caption" color="text" display="block">
                                                                {line.package_component?.name || line.trip_component?.name || "Trip Logistics"}
                                                                {line.merchant?.name ? ` · ${line.merchant.name}` : ""}
                                                                {line.pax_count != null ? ` · ${line.pax_count} pax` : ""}
                                                            </MDTypography>
                                                        </MDBox>

                                                        {/* 2nd child: status, box stays centered in the row, content inside pushed right */}
                                                        <MDBox flex={1} display="flex" justifyContent="flex-end">
                                                            <Chip
                                                                label={chipProps.label}
                                                                size="small"
                                                                color={chipProps.color}
                                                            />
                                                        </MDBox>

                                                        {/* 3rd child: resend email icon OR confirmed tick, pinned to the far right */}
                                                        {/* 3rd child: resend email icon / booked tick / cancelled icon, pinned to the far right */}
                                                        <MDBox flexShrink={0}>
                                                            {line.status === "Waiting For Merchant Acknowledgment" && (
                                                                <Tooltip title="Resend the service request email to the merchant">
                                                                    <span>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setResendConfirmLine(line);
                                                                            }}
                                                                            disabled={resendingLineId === line.id}
                                                                        >
                                                                            {resendingLineId === line.id ? (
                                                                                <CircularProgress size={16} />
                                                                            ) : (
                                                                                <EmailOutlinedIcon sx={{ fontSize: 18, color: "#1565C8" }} />
                                                                            )}
                                                                        </IconButton>
                                                                    </span>
                                                                </Tooltip>
                                                            )}

                                                            {line.status === "Booked" && (
                                                                <Tooltip title="Order line confirmed">
                                                                    <span>
                                                                        <IconButton size="small" disabled>
                                                                            <Icon sx={{ fontSize: 18, color: "#2E7D32" }}>check_circle_outline</Icon>
                                                                        </IconButton>
                                                                    </span>
                                                                </Tooltip>
                                                            )}

                                                            {line.status === "Cancelled" && (
                                                                <Tooltip title="Order line cancelled">
                                                                    <span>
                                                                        <IconButton size="small" disabled>
                                                                            <Icon sx={{ fontSize: 18, color: "#C62828" }}>cancel</Icon>
                                                                        </IconButton>
                                                                    </span>
                                                                </Tooltip>
                                                            )}
                                                        </MDBox>
                                                    </MDBox>
                                                );
                                            })}
                                        </MDBox>
                                    )}
                                </InfoCard>
                            </Grid>
                        )}

                        {isMainSectionVisible("Audit Information") && (
                            <Grid item xs={12} sx={{ order: mainSectionOrder("Audit Information") }}>
                                <InfoCard icon="fact_check" title="Audit Information">
                                    <Grid container rowSpacing={2.5} columnSpacing={2}>
                                        <DetailField
                                            label="Created Date"
                                            value={formatDateTime(order.created_at)}
                                            xs={6}
                                            md={3}
                                        />
                                        <DetailField
                                            label="Modified Date"
                                            value={formatDateTime(order.updated_at)}
                                            xs={6}
                                            md={3}
                                        />

                                        <Grid item xs={6} md={3}>
                                            <MDTypography variant="caption" color="text" fontWeight="medium" display="block" mb={0.5}>
                                                Created By
                                            </MDTypography>
                                            <MDTypography
                                                variant="button"
                                                fontWeight="bold"
                                                color={order.created_by ? "info" : "dark"}
                                                sx={order.created_by ? { cursor: "pointer", "&:hover": { textDecoration: "underline" } } : {}}
                                                onClick={() => order.created_by && navigate(`/users/${order.created_by}/profile`)}
                                            >
                                                {order.created_by_name || "—"}
                                            </MDTypography>
                                        </Grid>

                                        <Grid item xs={6} md={3}>
                                            <MDTypography variant="caption" color="text" fontWeight="medium" display="block" mb={0.5}>
                                                Modified By
                                            </MDTypography>
                                            <MDTypography
                                                variant="button"
                                                fontWeight="bold"
                                                color={order.updated_by ? "info" : "dark"}
                                                sx={order.updated_by ? { cursor: "pointer", "&:hover": { textDecoration: "underline" } } : {}}
                                                onClick={() => order.updated_by && navigate(`/users/${order.updated_by}/profile`)}
                                            >
                                                {order.updated_by_name || "—"}
                                            </MDTypography>
                                        </Grid>
                                    </Grid>
                                </InfoCard>
                            </Grid>
                        )}

                    </Grid>
                </Grid>

                {/* Right rail */}

                <Grid item xs={12} lg={4}>
                    <MDBox display="flex" flexDirection="column" gap={3}>
                        {showBookingTimeline && (
                            <InfoCard icon="history" title="Booking Timeline">
                                <BookingTimeline events={order.timeline || []} navigate={navigate} />
                            </InfoCard>
                        )}

                        {(order.payment?.supplements || []).length > 0 && (
                            <InfoCard
                                icon="add_shopping_cart"
                                title="Add-on Supplements"
                                headerAction={<SupplementChip label="Billed separately" />}
                            >
                                <SupplementRailCard
                                    supplements={order.payment.supplements}
                                    payment={order.payment}
                                    formatAmount={formatAmount}
                                    navigate={navigate}
                                    onSendPaymentEmail={isOrderCancelled ? undefined : (supp) => openPaymentLinkForInvoice(supp.invoice_id)}
                                />
                            </InfoCard>
                        )}

                        {/* Quick Actions — dark header spans the whole card, not just a strip */}

                        <Card sx={{ borderRadius: "12px", border: "1px solid #E8EDF5", boxShadow: "none", overflow: "hidden" }}>
                            <MDBox display="flex" alignItems="center" gap={1} px={2} py={isV2 ? 1.75 : 1.5} sx={isV2 ? { borderBottom: `1px solid ${v2t.border}` } : { bgcolor: "#0E2348" }}>
                                <Icon sx={{ fontSize: isV2 ? 18 : 20, color: isV2 ? v2t.text2 : "#fff" }}>bolt</Icon>
                                <MDTypography variant="button" fontWeight="bold" color={isV2 ? "dark" : "white"} sx={isV2 ? { fontSize: 15, fontWeight: 600 } : undefined}>
                                    Quick Actions
                                </MDTypography>
                            </MDBox>

                            <MDBox>
                                {/*<QuickActionRow*/}
                                {/*    icon="summarize"*/}
                                {/*    label="View Itinerary"*/}
                                {/*    onClick={() => setPdfPreviewOpen(true)}*/}
                                {/*/>*/}
                                <QuickActionRow
                                    icon="summarize"
                                    label={loadingItineraryPreview ? "Opening..." : "View Itinerary"}
                                    onClick={handleViewItinerary}
                                    loading={loadingItineraryPreview}
                                    disabled={isDraftOrder}
                                />
                                <QuickActionRow
                                    icon="send"
                                    label={sendingItinerary ? "Sending..." : "Send Itinerary to Customer"}
                                    onClick={handleSendItinerary}
                                    loading={sendingItinerary}
                                    disabled={isDraftOrder}
                                />
                                {!isOrderCancelled && (
                                    <QuickActionRow
                                        icon="payment"
                                        label={preparingPaymentLink ? "Preparing..." : "Send Payment Link"}
                                        onClick={handleSendPaymentLink}
                                        loading={preparingPaymentLink}
                                        disabled={isDraftOrder}
                                    />
                                )}
                                <QuickActionRow
                                    icon="sync"
                                    label="Resend Confirmation"
                                    onClick={() => { }}
                                    disabled={isDraftOrder}
                                />

                                {/* Update Status row */}
                                {/*<MDBox*/}
                                {/*    onClick={() => setStatusMenuOpen((prev) => !prev)}*/}
                                {/*    display="flex"*/}
                                {/*    alignItems="center"*/}
                                {/*    justifyContent="space-between"*/}
                                {/*    px={2}*/}
                                {/*    py={1.5}*/}
                                {/*    sx={{*/}
                                {/*        cursor: "pointer",*/}
                                {/*        borderBottom: "1px solid #E8EDF5",*/}
                                {/*        "&:hover": { bgcolor: "#FAFBFD" },*/}
                                {/*        transition: "background-color 0.15s ease",*/}
                                {/*    }}*/}
                                {/*>*/}
                                {/*    <MDBox display="flex" alignItems="center" gap={1.25}>*/}
                                {/*        <Icon sx={{ fontSize: 19, color: "#1565C8" }}>update</Icon>*/}
                                {/*        <MDTypography variant="button" fontWeight="medium" sx={{ color: "#1A2035" }}>*/}
                                {/*            Update Status*/}
                                {/*        </MDTypography>*/}
                                {/*    </MDBox>*/}
                                {/*    <Icon*/}
                                {/*        sx={{*/}
                                {/*            fontSize: 20,*/}
                                {/*            color: "#B0BAC9",*/}
                                {/*            transition: "transform 0.2s ease",*/}
                                {/*            transform: statusMenuOpen ? "rotate(180deg)" : "rotate(0deg)",*/}
                                {/*        }}*/}
                                {/*    >*/}
                                {/*        <KeyboardArrowDownIcon fontSize="inherit" />*/}
                                {/*    </Icon>*/}
                                {/*</MDBox>*/}

                                {/*<Collapse in={statusMenuOpen} timeout="auto" unmountOnExit>*/}
                                {/*    <MDBox sx={{ bgcolor: "#FAFBFD", borderBottom: "1px solid #E8EDF5" }}>*/}
                                {/*        {ORDER_STATUS_OPTIONS.map((opt) => (*/}
                                {/*            <StatusOptionRow*/}
                                {/*                key={opt.value}*/}
                                {/*                label={opt.label}*/}
                                {/*                active={order?.sub_status?.toLowerCase() === opt?.sub_status?.toLowerCase()}*/}
                                {/*                onClick={() => handleStatusSelect(opt.status, opt.sub_status)}*/}
                                {/*            />*/}
                                {/*        ))}*/}

                                {/*        {updatingStatus && (*/}
                                {/*            <MDBox display="flex" alignItems="center" gap={1} px={2} py={1}>*/}
                                {/*                <CircularProgress size={14} />*/}
                                {/*                <MDTypography variant="caption" color="text">*/}
                                {/*                    Updating...*/}
                                {/*                </MDTypography>*/}
                                {/*            </MDBox>*/}
                                {/*        )}*/}

                                {/*        {statusError && (*/}
                                {/*            <MDTypography variant="caption" sx={{ color: "#C62828" }} display="block" px={2} py={1}>*/}
                                {/*                {statusError}*/}
                                {/*            </MDTypography>*/}
                                {/*        )}*/}
                                {/*    </MDBox>*/}
                                {/*</Collapse>*/}

                                <QuickActionRow
                                    icon="cancel"
                                    label="Cancel Booking"
                                    danger
                                    isLast
                                    onClick={handleOpenCancellationDialog}
                                    disabled={isDraftOrder || isOrderCancelled}
                                />
                            </MDBox>
                        </Card>
                    </MDBox>
                </Grid>
            </Grid>

        </MDBox>

        {/* CANCELLATION DIALOG */}

        <Dialog
            open={cancellationDialog}
            onClose={handleCancellationDialogClose}
            maxWidth="lg"
            fullWidth
            keepMounted
            disableEscapeKeyDown
        >

            {/* Cancellation Dialog Title */}

            <DialogTitle px={2}>Cancel Booking</DialogTitle>

            {/* Cancellation Dialog Content*/}

            <DialogContent>

                {/* Section : Quick Booking Summary */}

                <MDBox px={2} py={1} borderRadius="md" border="1px solid" borderColor="divider">

                    {/* Section Title */}

                    <MDBox>
                        <MDTypography variant="h6" pt={2}>
                            Quick Booking Summary
                        </MDTypography>
                    </MDBox>

                    <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>

                        {/* Trip Name */}

                        <MDBox display="flex" gap={1}>
                            <MDBox
                                sx={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: "5%",
                                    bgcolor: "#E3F2FD",
                                    color: "#1565C8",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: 700,
                                    fontSize: "0.9rem",
                                    flexShrink: 0,
                                }}
                            >
                                <CalendarTodayOutlinedIcon fontSize="medium" />
                            </MDBox>
                            <MDBox display="flex" flexDirection="column" alignItems="flex-start">
                                <MDTypography variant="caption">
                                    Trip
                                </MDTypography>
                                <MDTypography variant="button">
                                    {order.trip.trip_name || "-"}
                                </MDTypography>
                            </MDBox>
                        </MDBox>

                        <Divider orientation="vertical" flexItem sx={{ height: 80, alignSelf: "center" }} />

                        {/* Order Number */}

                        <MDBox display="flex" flexDirection="column" alignItems="flex-start" mr="auto">
                            <MDTypography variant="caption">
                                Order Number
                            </MDTypography>
                            <MonoText variant="button">
                                {order.order_id}
                            </MonoText>
                        </MDBox>

                        <Divider orientation="vertical" flexItem sx={{ height: 80, alignSelf: "center" }} />

                        {/* Departure Date */}

                        <MDBox display="flex" flexDirection="column" alignItems="flex-start" mr="auto">
                            <MDTypography variant="caption">
                                Departure Date
                            </MDTypography>
                            <MDTypography variant="button">
                                {order.trip.departure_date}
                            </MDTypography>
                        </MDBox>

                        <Divider orientation="vertical" flexItem sx={{ height: 80, alignSelf: "center" }} />

                        {/* Travelers */}

                        <MDBox display="flex" flexDirection="column" alignItems="flex-start" mr="auto">
                            <MDTypography variant="caption">
                                Travellers
                            </MDTypography>
                            <MDTypography variant="button">
                                {order.travelers?.length || 0}
                            </MDTypography>
                            {order.travelers?.length > 0 && (
                                <MDTypography variant="caption" color="text">
                                    {formatTravelerMix(order.travelers)}
                                </MDTypography>
                            )}
                        </MDBox>

                        <Divider orientation="vertical" flexItem sx={{ height: 80, alignSelf: "center" }} />

                        {/* Total Amount Payable */}

                        <MDBox display="flex" flexDirection="column" alignItems="flex-start" mr="auto">
                            <MDTypography variant="caption">
                                Total Booking Amount
                            </MDTypography>
                            <MDTypography variant="button">
                                {formatAmount(paymentSummary.total)}
                            </MDTypography>
                        </MDBox>
                    </MDBox>

                </MDBox>

                {/* Section : Traveler Details */}

                <MDBox display="flex" flexDirection="column" gap={2} px={2} py={1} my={3} borderRadius="md" border="1px solid" borderColor="divider">
                    <MDBox pt={2}>
                        <MDTypography variant="h6" sx={{ lineHeight: 1 }}>
                            Traveler Details
                        </MDTypography>
                        <MDTypography variant="caption">Select the traveler(s) you want to cancel.</MDTypography>
                    </MDBox>
                    <TravelersTable
                        travelers={order.travelers}
                        selectable
                        selectedIds={selectedIds}
                        onSelectionChange={setSelectedIds}
                    />

                    {needsNewPrimary && (
                        <MDBox>
                            <FieldLabel
                                label="New Primary Traveler"
                                required
                                hint="The primary traveler is being cancelled. Choose who becomes the primary traveler."
                            />
                            <Autocomplete
                                size="small"
                                options={remainingTravelers.filter((t) => getTravelerType(t) !== "Child")}
                                getOptionLabel={(t) => t.name || ""}
                                isOptionEqualToValue={(a, b) => a.id === b.id}
                                value={remainingTravelers.find((t) => t.id === newPrimaryId) || null}
                                onChange={(_, value) => setNewPrimaryId(value?.id || null)}
                                renderInput={(params) => <TextField {...params} placeholder="Select new primary traveler" />}
                            />
                        </MDBox>
                    )}

                    {/* Cancellation Type */}

                    <MDBox>
                        <FieldLabel label="Cancellation Type" required />
                        <Autocomplete
                            size="small"
                            options={["By Customer", "By Organization", "Non-Payment"]}
                            value={cancellationType}
                            onChange={(_, value) => setCancellationType(value)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Select Cancellation Type"
                                />
                            )}
                        />
                    </MDBox>

                    {/* Comment */}

                    <MDBox flex="1 1 100%">
                        <FieldLabel
                            label="Comments / Reason (Optional)"
                            hint="Add any additional notes or reason for cancellation."
                        />
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            placeholder="Enter optional comments or cancellation reason..."
                            value={cancellationComments}
                            onChange={(e) => setCancellationComments(e.target.value)}
                        />
                    </MDBox>

                </MDBox>

                {/* Section : Applicable Cancellation Term */}

                <MDBox display="flex" flexDirection="column" gap={2} px={2} py={2} mb={1} borderRadius="md" border="1px solid" borderColor="divider">
                    <MDBox>
                        <MDTypography variant="h6" sx={{ lineHeight: 1 }}>
                            Applicable Cancellation Term
                        </MDTypography>
                        <MDTypography variant="caption">
                            Based on today&apos;s date, the departure date, the cancellation type and the amount paid.
                            Final amounts are calculated when the cancellation is submitted.
                        </MDTypography>
                    </MDBox>

                    {(!selectedIds.length || !cancellationType) && (
                        <MDTypography variant="caption" color="text" sx={{ fontStyle: "italic", opacity: 0.7 }}>
                            Select the traveler(s) and a cancellation type to see the applicable term.
                        </MDTypography>
                    )}

                    {cancellationPreviewLoading && (
                        <MDBox display="flex" alignItems="center" gap={1}>
                            <CircularProgress size={16} />
                            <MDTypography variant="caption" color="text">Evaluating cancellation terms...</MDTypography>
                        </MDBox>
                    )}

                    {!cancellationPreviewLoading && cancellationPreviewError && (
                        <Alert severity="warning">{cancellationPreviewError}</Alert>
                    )}

                    {!cancellationPreviewLoading && cancellationPreview && (() => {
                        const p = cancellationPreview;
                        const previewCurrency = p.currency || orderCurrency;
                        const term = p.applicable_term;
                        const outcomeStyle = REFUND_OUTCOME_STYLES[p.outcome] || REFUND_OUTCOME_STYLES["No Refund"];
                        const refundRule = term
                            ? (term.amount_percent !== null && term.amount_percent !== undefined
                                ? `${term.amount_percent}% refund`
                                : term.amount_actual !== null && term.amount_actual !== undefined
                                    ? `${formatAmount(term.amount_actual, previewCurrency)} flat refund`
                                    : "—")
                            : "—";

                        return (
                            <>
                                {p.blocking_error && <Alert severity="error">{p.blocking_error}</Alert>}
                                {(p.warnings || []).map((w) => (
                                    <Alert key={w} severity="warning">{w}</Alert>
                                ))}

                                <Grid container rowSpacing={2.5} columnSpacing={2}>
                                    <DetailField
                                        label="Days Before Departure"
                                        value={p.days_remaining !== null && p.days_remaining !== undefined ? `${p.days_remaining} days` : "—"}
                                        xs={6} md={3}
                                    />
                                    <DetailField
                                        label="Applicable Rule"
                                        value={term ? `${term.name} (from ${term.days ?? 0} days)` : "No rule matched"}
                                        xs={6} md={3}
                                    />
                                    <DetailField label="Refund Rule" value={refundRule} xs={6} md={3} />
                                    <DetailField
                                        label="Refund Outcome"
                                        value={
                                            <Chip
                                                size="small"
                                                label={p.outcome}
                                                sx={{ bgcolor: outcomeStyle.bg, color: outcomeStyle.color, fontWeight: 700, fontSize: "0.7rem" }}
                                            />
                                        }
                                        xs={6} md={3}
                                    />
                                    {p.outcome === "Coupon" && p.coupon_expiry_date && (
                                        <DetailField
                                            label="Coupon Valid Until"
                                            value={`${formatDateOnly(p.coupon_expiry_date)} (${p.coupon_expiry_days} days)`}
                                            xs={6} md={3}
                                        />
                                    )}
                                    <DetailField label="Paid on Order" value={formatAmount(Number(p.total_paid), previewCurrency)} xs={6} md={3} />
                                </Grid>

                                {/* Per-traveler breakdown */}
                                <MDBox sx={{ border: "1px solid #E8EDF5", borderRadius: "0.75rem", overflow: "auto" }}>
                                    <MDBox
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: "1.8fr 1fr 1fr 1fr 1fr 1fr 1.1fr",
                                            bgcolor: "#F4F6F9",
                                            borderBottom: "1px solid #E8EDF5",
                                            px: 2,
                                            py: 1,
                                            minWidth: "max-content",
                                        }}
                                    >
                                        {["TRAVELER", "SHARED GROSS", "ADD-ON GROSS", "PAID SHARE", "DEDUCTION", "REFUND", "UNPAID CUT"].map((col) => (
                                            <MDTypography key={col} variant="caption" fontWeight="bold" color="text">
                                                {col}
                                            </MDTypography>
                                        ))}
                                    </MDBox>

                                    {p.travelers.map((t) => (
                                        <MDBox
                                            key={t.pax_id}
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: "1.8fr 1fr 1fr 1fr 1fr 1fr 1.1fr",
                                                px: 2,
                                                py: 1,
                                                minWidth: "max-content",
                                                borderBottom: "1px solid #E8EDF5",
                                            }}
                                        >
                                            <MDTypography variant="button" color="dark">
                                                {t.name}{t.is_primary ? " (Primary)" : ""}
                                            </MDTypography>
                                            <MDTypography variant="button" color="text">
                                                {t.shared_amount != null ? formatAmount(Number(t.shared_amount)) : "-"}
                                            </MDTypography>
                                            <MDTypography variant="button" color="text">
                                                {t.addon_amount != null ? formatAmount(Number(t.addon_amount)) : "-"}
                                            </MDTypography>
                                            <MDTypography variant="button" color="text">{formatAmount(Number(t.paid_share))}</MDTypography>
                                            <MDTypography variant="button" color="text">{formatAmount(Number(t.deduction))}</MDTypography>
                                            <MDTypography variant="button" fontWeight="bold" color="dark">
                                                {formatAmount(Number(t.refund_amount))}
                                            </MDTypography>
                                            <MDTypography variant="button" color="text">
                                                {t.unpaid_reduction != null ? formatAmount(Number(t.unpaid_reduction)) : "-"}
                                            </MDTypography>
                                        </MDBox>
                                    ))}

                                    <MDBox
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: "1.8fr 1fr 1fr 1fr 1fr 1fr 1.1fr",
                                            px: 2,
                                            py: 1,
                                            minWidth: "max-content",
                                            bgcolor: "#FAFBFD",
                                        }}
                                    >
                                        <MDTypography variant="button" fontWeight="bold" color="dark">Total</MDTypography>
                                        <span />
                                        <span />
                                        <span />
                                        <MDTypography variant="button" fontWeight="bold" color="dark">
                                            {formatAmount(Number(p.total_deduction))}
                                        </MDTypography>
                                        <MDTypography variant="button" fontWeight="bold" color="dark">
                                            {formatAmount(Number(p.total_refund))}
                                        </MDTypography>
                                        <MDTypography variant="button" fontWeight="bold" color="dark">
                                            {formatAmount(p.travelers.reduce((acc, t) => acc + Number(t.unpaid_reduction || 0), 0))}
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>
                                {p.new_order_total != null && (
                                    <MDBox sx={{ mt: 1.5, display: "flex", justifyContent: "flex-end" }}>
                                        <MDTypography variant="caption" color="text">
                                            New Order Total after cancellation: <strong>{formatAmount(Number(p.new_order_total))}</strong>
                                        </MDTypography>
                                    </MDBox>
                                )}
                            </>
                        );
                    })()}
                </MDBox>

            </DialogContent>

            {/* Cancellation Dialog Action Button */}

            <DialogActions>

                {/* Close Button */}

                <MDButton
                    onClick={handleCancellationDialogClose}
                    variant="outlined"
                    color="secondary"
                    disabled={cancellationInProgress}
                >
                    Close
                </MDButton>

                {/* Cancel Booking Button */}

                <MDButton
                    onClick={handleCancellation}
                    color="error"
                    disabled={cancellationInProgress || Boolean(cancellationPreview?.blocking_error)}
                    startIcon={cancellationInProgress && <CircularProgress size={20} color="inherit" />}
                >
                    {cancellationInProgress ? 'Cancellation in progress...' : 'CANCEL BOOKING'}
                </MDButton>
            </DialogActions>
        </Dialog>

        <Dialog
            open={!!resendConfirmLine}
            onClose={() => !resendingLineId && setResendConfirmLine(null)}
            maxWidth="xs"
            fullWidth
        >
            <DialogTitle>Resend Service Request Email</DialogTitle>
            <DialogContent>
                <MDTypography variant="button" color="text">
                    Send the service request email again for{" "}
                    <MDTypography component="span" variant="button" fontWeight="bold">
                        {resendConfirmLine?.name || `MOL ${resendConfirmLine?.id}`}
                    </MDTypography>
                    {resendConfirmLine?.merchant?.name ? ` to ${resendConfirmLine.merchant.name}` : ""}?
                </MDTypography>
            </DialogContent>
            <DialogActions>
                <MDButton
                    onClick={() => setResendConfirmLine(null)}
                    variant="outlined"
                    color="secondary"
                    disabled={resendingLineId === resendConfirmLine?.id}
                >
                    Cancel
                </MDButton>
                <MDButton
                    onClick={async () => {
                        const lineId = resendConfirmLine.id;
                        await handleResendLineEmail(lineId);
                        setResendConfirmLine(null);
                    }}
                    color="info"
                    disabled={resendingLineId === resendConfirmLine?.id}
                    startIcon={resendingLineId === resendConfirmLine?.id && <CircularProgress size={16} color="inherit" />}
                >
                    {resendingLineId === resendConfirmLine?.id ? "Sending..." : "Confirm & Send"}
                </MDButton>
            </DialogActions>
        </Dialog>

        <ItineraryPreviewDialog
            open={pdfPreviewOpen}
            onClose={() => setPdfPreviewOpen(false)}
            orderId={order.id}
            orderLabel={order.order_id}
        />

        <SendPaymentLinkModal
            open={paymentLinkModalOpen}
            onClose={() => setPaymentLinkModalOpen(false)}
            invoiceId={paymentLinkInvoiceId}
            defaultEmail={paymentLinkDefaultEmail}
            title="Send payment link"
            updateSnackbar={setSnackbar}
            isSupplement={(order?.payment?.supplements || []).some(s => String(s.invoice_id) === String(paymentLinkInvoiceId))}
            onSent={() => {
                setPaymentLinkModalOpen(false);
                setSnackbar({
                    open: true,
                    message: "Payment link email sent to the customer.",
                    severity: "success",
                });
                if (order?.id) loadPaymentScheduleForOrder(order.id);
            }}
        />

        <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={() => setSnackbar({ open: false, message: '', severity: 'success' })}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
            <Alert
                severity={snackbar.severity}
                onClose={() => setSnackbar({ open: false, message: '', severity: 'success' })}
            >
                {snackbar.message}
            </Alert>
        </Snackbar>

    </DashboardLayout>
);
}

export default TravelOrderDetailView;
